import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, ArrowRight, Users, Shield, CheckCircle, FileSpreadsheet, UserCog, ChevronRight, Trash2, Mail, Send, Check, X, RefreshCw, RefreshCcw, Eye, EyeOff, Receipt, Search, RotateCcw, FileText, Calendar, Megaphone, MessageSquare, Gift, CreditCard, Clock, Plus, History, Tag, AlertTriangle, UserPlus, Store, Palette, Edit, MapPin, Navigation, ExternalLink, Database, Wrench, LogOut, Bell, DollarSign, Trophy, GraduationCap, MessageCircle, BookOpen, Shirt, Inbox, QrCode, Layers, Image } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import PaymentTracking from "@/components/PaymentTracking";
import EnvironmentBanner from "@/components/EnvironmentBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Credit Management Component
function CreditManagement() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerCredits, setPlayerCredits] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [grantForm, setGrantForm] = useState({
    player_id: "",
    credit_type: "session",  // "session" or "monetary"
    credits: 1,
    amount: "",  // For monetary credits
    session_price: "",  // Reference price for session credits
    reason: "admin_grant",
    reason_details: ""
  });
  const [bulkForm, setBulkForm] = useState({
    target_type: "term",
    term_id: "",
    team_id: "",
    credit_amount: "",
    reason: "rate_change",
    reason_details: "",
    expires_months: 6
  });
  const [granting, setGranting] = useState(false);
  // Oct 2026: pick the child's real training dates instead of typing a count
  const [playerSessions, setPlayerSessions] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [pickedDates, setPickedDates] = useState([]);
  const [manualCount, setManualCount] = useState(false);
  const [bulkGranting, setBulkGranting] = useState(false);
  const [players, setPlayers] = useState([]);
  const [terms, setTerms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [pendingMCs, setPendingMCs] = useState([]);
  const [mcLoading, setMcLoading] = useState(false);
  const [reviewingMC, setReviewingMC] = useState(null);

  useEffect(() => {
    fetchSummary();
    fetchPlayers();
    fetchTerms();
    fetchTeams();
    fetchPendingMCs();
  }, []);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/admin/credits/summary`, { withCredentials: true });
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching credits summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/academy/admin/players`, { withCredentials: true });
      setPlayers(response.data || []);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      setTerms(response.data || []);
    } catch (error) {
      console.error("Error fetching terms:", error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/academy/teams`, { withCredentials: true });
      setTeams(response.data || []);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const fetchPendingMCs = async () => {
    try {
      setMcLoading(true);
      const response = await axios.get(`${API}/academy/admin/mc/pending`, { withCredentials: true });
      setPendingMCs(response.data?.pending_mcs || []);
    } catch (error) {
      console.error("Error fetching pending MCs:", error);
    } finally {
      setMcLoading(false);
    }
  };

  const fetchPlayerCredits = async (playerId) => {
    try {
      const response = await axios.get(`${API}/academy/parent/credits/${playerId}/history`, { withCredentials: true });
      setPlayerCredits(response.data);
      setSelectedPlayer(playerId);
    } catch (error) {
      console.error("Error fetching player credits:", error);
      toast.error("Failed to load player credits");
    }
  };

  // Load the child's actual training dates + the price they were billed per session
  useEffect(() => {
    if (!grantForm.player_id) { setPlayerSessions(null); setPickedDates([]); return; }
    let cancelled = false;
    (async () => {
      setSessionsLoading(true);
      setPickedDates([]);
      try {
        const r = await axios.get(`${API}/academy/admin/credits/player-sessions/${grantForm.player_id}`, { withCredentials: true });
        if (cancelled) return;
        setPlayerSessions(r.data);
        if (r.data?.default_session_price != null) {
          setGrantForm((f) => ({ ...f, session_price: String(r.data.default_session_price) }));
        }
      } catch (e) {
        if (!cancelled) setPlayerSessions(null);
      } finally {
        if (!cancelled) setSessionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [grantForm.player_id]);

  const toggleDate = (s) => {
    if (s.credited_by) return;
    setPickedDates((prev) => {
      const next = prev.some((p) => p.date === s.date)
        ? prev.filter((p) => p.date !== s.date)
        : [...prev, { session_id: s.session_id, date: s.date, unit_price: s.unit_price }];
      setGrantForm((f) => ({ ...f, credits: next.length || 1 }));
      return next;
    });
  };

  const grantCredit = async () => {
    if (!grantForm.player_id) {
      toast.error("Please select a player");
      return;
    }
    
    // Validate based on credit type
    if (grantForm.credit_type === "monetary") {
      if (!grantForm.amount || parseFloat(grantForm.amount) <= 0) {
        toast.error("Please enter a valid amount");
        return;
      }
    } else {
      if (!grantForm.credits || grantForm.credits < 1) {
        toast.error("Please enter at least 1 session credit");
        return;
      }
    }
    
    // Require reason_details if "other" is selected
    if (grantForm.reason === "other" && !grantForm.reason_details?.trim()) {
      toast.error("Please specify the reason in the notes field");
      return;
    }
    
    setGranting(true);
    try {
      const payload = {
        ...grantForm,
        amount: grantForm.credit_type === "monetary" ? parseFloat(grantForm.amount) : null,
        session_price: grantForm.session_price ? parseFloat(grantForm.session_price) : null,
        session_refs: grantForm.credit_type === "session" && pickedDates.length ? pickedDates : null,
        send_email: grantForm.send_email || false,
        include_calculation: grantForm.include_calculation || false,
        calculation_params: grantForm.include_calculation ? {
          sessions_per_week: grantForm.calc_sessions || 1,
          term_weeks: grantForm.calc_weeks || 10,
          calculated_total: parseFloat(grantForm.calc_total) || 0,
          actual_paid: parseFloat(grantForm.calc_paid) || 0,
          credit_amount: parseFloat(grantForm.amount) || 0
        } : null
      };
      
      await axios.post(`${API}/academy/admin/credits/grant`, payload, { withCredentials: true });
      
      const successMsg = grantForm.credit_type === "monetary" 
        ? `Granted $${grantForm.amount} credit successfully`
        : `Granted ${grantForm.credits} session credit(s) successfully`;
      toast.success(successMsg);
      if (grantForm.send_email) {
        toast.success("Email notification sent to parent");
      }
      
      setShowGrantModal(false);
      setGrantForm({ player_id: "", credit_type: "session", credits: 1, amount: "", session_price: "", reason: "admin_grant", reason_details: "", send_email: false, include_calculation: false });
      setPickedDates([]);
      setPlayerSessions(null);
      fetchSummary();
      if (selectedPlayer) fetchPlayerCredits(selectedPlayer);
    } catch (error) {
      console.error("Error granting credit:", error);
      toast.error(error.response?.data?.detail || "Failed to grant credit");
    } finally {
      setGranting(false);
    }
  };

  const bulkGrantCredits = async () => {
    if (!bulkForm.credit_amount || parseFloat(bulkForm.credit_amount) <= 0) {
      toast.error("Please enter a valid credit amount");
      return;
    }
    if (bulkForm.target_type === "term" && !bulkForm.term_id) {
      toast.error("Please select a term");
      return;
    }
    if (bulkForm.target_type === "team" && !bulkForm.team_id) {
      toast.error("Please select a team");
      return;
    }
    if (!bulkForm.reason_details) {
      toast.error("Please enter a reason");
      return;
    }

    setBulkGranting(true);
    try {
      const payload = {
        credit_amount: parseFloat(bulkForm.credit_amount),
        reason: bulkForm.reason,
        reason_details: bulkForm.reason_details,
        expires_months: bulkForm.expires_months
      };
      if (bulkForm.target_type === "term") payload.term_id = bulkForm.term_id;
      if (bulkForm.target_type === "team") payload.team_id = bulkForm.team_id;

      const response = await axios.post(`${API}/academy/admin/credits/bulk-grant`, payload, { withCredentials: true });
      toast.success(response.data.message);
      setShowBulkModal(false);
      setBulkForm({ target_type: "term", term_id: "", team_id: "", credit_amount: "", reason: "rate_change", reason_details: "", expires_months: 6 });
      fetchSummary();
    } catch (error) {
      console.error("Error bulk granting credits:", error);
      toast.error(error.response?.data?.detail || "Failed to grant credits");
    } finally {
      setBulkGranting(false);
    }
  };

  const reviewMC = async (mcId, action, notes = "") => {
    try {
      await axios.post(`${API}/academy/admin/mc/${mcId}/review?action=${action}&notes=${encodeURIComponent(notes)}`, {}, { withCredentials: true });
      toast.success(`MC ${action}d successfully`);
      setReviewingMC(null);
      fetchPendingMCs();
      fetchSummary();
    } catch (error) {
      console.error("Error reviewing MC:", error);
      toast.error(error.response?.data?.detail || "Failed to review MC");
    }
  };

  const reasonLabels = {
    mc_approved: "Medical Certificate",
    session_cancelled: "Session Cancelled",
    admin_grant: "Admin Grant",
    makeup_earned: "Makeup Earned",
    rate_change: "Rate Change"
  };

  const filteredPlayers = players.filter(p => 
    !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2">
        <Button
          variant={activeTab === "overview" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("overview")}
          className={activeTab === "overview" ? "bg-yellow-500/100 text-black" : "text-slate-400"}
        >
          <CreditCard className="w-4 h-4 mr-2" /> Overview
        </Button>
        <Button
          variant={activeTab === "mc-review" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("mc-review")}
          className={activeTab === "mc-review" ? "bg-yellow-500/100 text-black" : "text-slate-400"}
        >
          <FileText className="w-4 h-4 mr-2" /> MC Review
          {pendingMCs.length > 0 && (
            <span className="ml-2 bg-red-500/100 text-white text-xs px-2 py-0.5 rounded-full">{pendingMCs.length}</span>
          )}
        </Button>
        <Button
          variant={activeTab === "bulk" ? "default" : "ghost"}
          size="sm"
          onClick={() => setActiveTab("bulk")}
          className={activeTab === "bulk" ? "bg-yellow-500/100 text-black" : "text-slate-400"}
        >
          <Users className="w-4 h-4 mr-2" /> Bulk Credits
        </Button>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-500/20 rounded-full">
                  <CreditCard className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{summary?.total_session_credits || 0}</p>
                  <p className="text-sm text-slate-400">Session Credits</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <span className="text-blue-400 text-lg font-bold">$</span>
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">${(summary?.total_monetary_credits || 0).toFixed(2)}</p>
                  <p className="text-sm text-slate-400">Monetary Credits</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-500/20 rounded-full">
                  <Users className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{summary?.total_players_with_credits || 0}</p>
                  <p className="text-sm text-slate-400">Players with Credits</p>
                </div>
              </div>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30">
              <Button 
                onClick={() => setShowGrantModal(true)}
                className="w-full h-full bg-purple-600 hover:bg-purple-500/100 text-white"
                data-testid="grant-credit-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Grant Credit
              </Button>
            </Card>
            <Card className="p-4 bg-gradient-to-br from-sky-500/20 to-sky-600/10 border border-sky-500/30">
              <Link to="/admin/rain-out" className="block w-full h-full" data-testid="credit-rainout-link">
                <Button className="w-full h-full bg-sky-600 hover:bg-sky-500 text-white">
                  <Calendar className="w-5 h-5 mr-2" />
                  Call off a session day
                </Button>
              </Link>
            </Card>
          </div>

          {/* Grant Credit Modal */}
          {showGrantModal && (
            <Card className="p-6 bg-slate-900 border border-purple-500/30 rounded-xl">
              <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-400" />
                Grant Credit
              </h4>
              <div className="space-y-4">
                {/* Player Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Search Player</label>
                  <Input
                    placeholder="Type to search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white mb-2"
                  />
                  <Select value={grantForm.player_id} onValueChange={(v) => setGrantForm({...grantForm, player_id: v})}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                      <SelectValue placeholder="Choose a player..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {filteredPlayers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.age_group || "No group"})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Credit Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Credit Type</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => setGrantForm({...grantForm, credit_type: "session"})}
                      className={`flex-1 ${grantForm.credit_type === "session" ? 'bg-purple-600' : 'bg-slate-700'}`}
                    >
                      Session Credit
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setGrantForm({...grantForm, credit_type: "monetary"})}
                      className={`flex-1 ${grantForm.credit_type === "monetary" ? 'bg-emerald-600' : 'bg-slate-700'}`}
                    >
                      Dollar Credit
                    </Button>
                  </div>
                </div>

                {/* Session Credit Fields */}
                {grantForm.credit_type === "session" && (
                  <div className="space-y-3">
                    {!grantForm.player_id && (
                      <p className="text-xs text-slate-500">Pick a player to see their training dates.</p>
                    )}
                    {grantForm.player_id && sessionsLoading && (
                      <p className="text-xs text-slate-400 flex items-center gap-2" data-testid="credit-sessions-loading">
                        <RefreshCw className="w-3 h-3 animate-spin" /> Loading their training dates…
                      </p>
                    )}

                    {grantForm.player_id && !sessionsLoading && !manualCount && (
                      <div data-testid="credit-date-picker">
                        {(playerSessions?.terms || []).filter((t) => t.sessions.length).length === 0 ? (
                          <p className="text-xs text-amber-300">
                            No training dates found for this player — enter a session count manually below.
                          </p>
                        ) : (
                          (playerSessions.terms || []).filter((t) => t.sessions.length).map((t) => (
                            <div key={t.term_id + t.enrolment_player_id} className="mb-3" data-testid={`credit-term-${t.term_id}`}>
                              <p className="text-xs text-slate-300 mb-1">
                                {t.term_name}
                                <span className="text-slate-500">
                                  {" "}· ${t.default_session_price ?? "?"}/session
                                  {t.payment_status ? ` · ${t.payment_status}` : ""}
                                </span>
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {t.sessions.map((s) => {
                                  const on = pickedDates.some((p) => p.date === s.date);
                                  return (
                                    <button
                                      key={s.session_id + s.date}
                                      type="button"
                                      onClick={() => toggleDate(s)}
                                      disabled={!!s.credited_by}
                                      title={s.credited_by ? "Already credited" : `${s.location || ""} ${s.start_time || ""} · $${s.unit_price}`}
                                      className={`text-[11px] px-2 py-1 rounded border transition-colors ${
                                        s.credited_by
                                          ? "bg-slate-800/40 border-slate-700 text-slate-500 cursor-not-allowed line-through"
                                          : on
                                          ? "bg-purple-600 border-purple-400 text-white"
                                          : s.cancelled
                                          ? "bg-amber-500/10 border-amber-500/40 text-amber-200 hover:bg-amber-500/20"
                                          : "bg-slate-800 border-slate-600 text-slate-200 hover:bg-slate-700"
                                      }`}
                                      data-testid={`credit-date-chip-${s.date}`}
                                    >
                                      {s.label}
                                      {s.cancelled ? " · cancelled" : ""}
                                      {s.credited_by ? " · credited" : ""}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))
                        )}
                        <button
                          type="button"
                          onClick={() => setManualCount(true)}
                          className="text-[11px] text-slate-400 underline"
                          data-testid="credit-manual-toggle"
                        >
                          enter a session count manually instead
                        </button>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          Sessions {pickedDates.length > 0 && !manualCount && <span className="text-purple-300">(from dates)</span>}
                        </label>
                        {pickedDates.length > 0 && !manualCount ? (
                          <div className="h-10 flex items-center px-3 rounded-md bg-slate-800/50 border border-slate-700 text-white font-mono" data-testid="credit-count-derived">
                            {pickedDates.length}
                          </div>
                        ) : (
                          <Select value={String(grantForm.credits)} onValueChange={(v) => setGrantForm({...grantForm, credits: parseInt(v)})}>
                            <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n} Session{n>1?'s':''}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Session Price</label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="e.g., 50.00"
                          value={grantForm.session_price}
                          onChange={(e) => setGrantForm({...grantForm, session_price: e.target.value})}
                          className="bg-slate-800/50 border-slate-700 text-white"
                          data-testid="credit-session-price"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {playerSessions?.default_session_price != null
                            ? `Auto-filled from their term rate ($${playerSessions.default_session_price}) — override if needed`
                            : "Price per session at time of issue"}
                        </p>
                      </div>
                    </div>

                    {(pickedDates.length > 0 || grantForm.session_price) && (
                      <p className="text-sm text-slate-300" data-testid="credit-total-preview">
                        Crediting{" "}
                        <span className="text-purple-300 font-semibold">
                          {pickedDates.length && !manualCount ? pickedDates.length : grantForm.credits} session(s)
                        </span>
                        {grantForm.session_price && (
                          <> worth <span className="font-mono text-teal-300">
                            ${((pickedDates.length && !manualCount ? pickedDates.length : grantForm.credits) * parseFloat(grantForm.session_price || 0)).toFixed(2)}
                          </span></>
                        )}
                        {pickedDates.length > 0 && !manualCount && (
                          <span className="text-slate-400"> — {pickedDates.map((p) => p.date).sort().join(", ")}</span>
                        )}
                      </p>
                    )}
                  </div>
                )}

                {/* Monetary Credit Fields */}
                {grantForm.credit_type === "monetary" && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Amount ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="e.g., 25.00"
                      value={grantForm.amount}
                      onChange={(e) => setGrantForm({...grantForm, amount: e.target.value})}
                      className="bg-slate-800/50 border-slate-700 text-white"
                    />
                    <p className="text-xs text-slate-500 mt-1">Dollar amount for overpayments, refunds, etc.</p>
                  </div>
                )}
                
                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
                  <Select value={grantForm.reason} onValueChange={(v) => setGrantForm({...grantForm, reason: v})}>
                    <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin_grant">Admin Grant</SelectItem>
                      <SelectItem value="session_cancelled">Session Cancelled</SelectItem>
                      <SelectItem value="mc_approved">Medical Certificate</SelectItem>
                      <SelectItem value="makeup_earned">Makeup Earned</SelectItem>
                      <SelectItem value="overpayment">Overpayment</SelectItem>
                      <SelectItem value="rate_change">Rate Adjustment</SelectItem>
                      <SelectItem value="refund">Refund</SelectItem>
                      <SelectItem value="other">Other (specify below)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    {grantForm.reason === "other" ? "Reason Details (required)" : "Notes"}
                  </label>
                  <Input
                    placeholder={grantForm.reason === "other" ? "Enter specific reason..." : "e.g., Refund for cancelled sessions due to weather"}
                    value={grantForm.reason_details}
                    onChange={(e) => setGrantForm({...grantForm, reason_details: e.target.value})}
                    className="bg-slate-800/50 border-slate-700 text-white"
                    required={grantForm.reason === "other"}
                  />
                </div>

                {/* Notification Options */}
                <div className="p-3 bg-slate-800/30 rounded-lg border border-slate-700">
                  <label className="block text-sm font-medium text-slate-300 mb-2">Notification Options</label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="send_email"
                        checked={grantForm.send_email || false}
                        onChange={(e) => setGrantForm({...grantForm, send_email: e.target.checked})}
                        className="w-4 h-4 rounded border-slate-600 bg-slate-700"
                      />
                      <label htmlFor="send_email" className="text-sm text-slate-300 cursor-pointer">
                        Send email notification to parent
                      </label>
                    </div>
                    {grantForm.send_email && grantForm.credit_type === "monetary" && (
                      <div className="flex items-center gap-2 ml-6">
                        <input
                          type="checkbox"
                          id="include_calculation"
                          checked={grantForm.include_calculation || false}
                          onChange={(e) => setGrantForm({...grantForm, include_calculation: e.target.checked})}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-700"
                        />
                        <label htmlFor="include_calculation" className="text-sm text-slate-300 cursor-pointer">
                          Include fee calculation breakdown
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <Button onClick={grantCredit} disabled={granting} className="flex-1 bg-purple-600 hover:bg-purple-500/100">
                    {granting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
                    {grantForm.credit_type === "monetary" ? `Grant $${grantForm.amount || '0'}` : `Grant ${grantForm.credits} Session(s)`}
                  </Button>
                  <Button onClick={() => setShowGrantModal(false)} variant="outline" className="border-slate-600 text-slate-300">
                    Cancel
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Players with Credits */}
          <Card className="p-4 bg-slate-900/50 border border-white/5 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-lg text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-400" />
                Players with Credit Balance
              </h4>
              <Button variant="ghost" size="sm" onClick={fetchSummary} className="text-slate-400 hover:text-white">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            {(!summary?.players || summary.players.length === 0) ? (
              <p className="text-slate-400 text-center py-8">No players have outstanding credits</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {summary.players.map((player) => (
                  <div 
                    key={player.player_id} 
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedPlayer === player.player_id 
                        ? 'bg-emerald-500/20 border-emerald-500/50' 
                        : 'bg-slate-800/50 border-white/5 hover:border-emerald-500/30'
                    }`}
                    onClick={() => fetchPlayerCredits(player.player_id)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-white">{player.player_name}</p>
                        <p className="text-xs text-slate-400">
                          Sessions: {player.session_credits} | Money: ${(player.monetary_credits || 0).toFixed(2)}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Player Credit History */}
          {playerCredits && (
            <Card className="p-4 bg-slate-900/50 border border-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-bold text-lg text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-blue-400" />
                  Credit History: {playerCredits.player_name}
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setSelectedPlayer(null); setPlayerCredits(null); }}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-400">{playerCredits.session_credits?.balance || 0}</p>
                  <p className="text-xs text-slate-400">Session Credits</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-400">${(playerCredits.monetary_credits?.balance || 0).toFixed(2)}</p>
                  <p className="text-xs text-slate-400">Monetary Credits</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-400">{playerCredits.mc_allowance?.remaining || 0}</p>
                  <p className="text-xs text-slate-400">MCs Remaining</p>
                </div>
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {playerCredits.credit_history?.map((record) => (
                  <div 
                    key={record.id} 
                    className={`p-3 rounded-lg border ${
                      record.type === 'grant' 
                        ? 'bg-emerald-500/10 border-emerald-500/20' 
                        : 'bg-amber-500/10 border-amber-500/20'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white flex items-center gap-2">
                          {record.type === 'grant' ? (
                            <Plus className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-400" />
                          )}
                          {record.credit_type === 'monetary' 
                            ? `${record.type === 'grant' ? '+' : '-'}$${(record.amount || 0).toFixed(2)}`
                            : `${record.type === 'grant' ? '+' : '-'}${record.credits} Credit`
                          }
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {reasonLabels[record.reason] || record.reason}
                          {record.reason_details && `: ${record.reason_details}`}
                        </p>
                      </div>
                      <span className="text-xs text-slate-400">
                        {new Date(record.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/* MC Review Tab */}
      {activeTab === "mc-review" && (
        <Card className="p-4 bg-slate-900/50 border border-white/5 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-lg text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-400" />
              Pending Medical Certificates
            </h4>
            <Button variant="ghost" size="sm" onClick={fetchPendingMCs} className="text-slate-400 hover:text-white">
              <RefreshCw className={`w-4 h-4 ${mcLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {pendingMCs.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-slate-400">No pending MCs to review</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingMCs.map((mc) => (
                <div key={mc.id} className="p-4 bg-slate-800/50 rounded-lg border border-amber-500/20">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium text-white">{mc.player_name}</p>
                      <p className="text-sm text-slate-400">MC Date: {mc.mc_date}</p>
                      <p className="text-xs text-slate-400">Term: {mc.term_name}</p>
                      <p className="text-xs text-slate-400">MC #{mc.mc_number} of {mc.mc_limit} allowed</p>
                      {mc.reason && <p className="text-xs text-slate-400 mt-1">Reason: {mc.reason}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-slate-400">Submitted</p>
                      <p className="text-sm text-white">{new Date(mc.submitted_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  {mc.file_name && (
                    <div className="mb-3 p-2 bg-slate-900/50 rounded flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-400" />
                      <span className="text-sm text-slate-300">{mc.file_name}</span>
                    </div>
                  )}

                  {reviewingMC === mc.id ? (
                    <div className="space-y-2">
                      <Input
                        placeholder="Review notes (optional)"
                        id={`mc-notes-${mc.id}`}
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-600 hover:bg-emerald-500/100"
                          onClick={() => {
                            const notes = document.getElementById(`mc-notes-${mc.id}`).value;
                            reviewMC(mc.id, "approve", notes);
                          }}
                        >
                          <Check className="w-4 h-4 mr-1" /> Approve (+1 Credit)
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1"
                          onClick={() => {
                            const notes = document.getElementById(`mc-notes-${mc.id}`).value;
                            reviewMC(mc.id, "reject", notes);
                          }}
                        >
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setReviewingMC(null)}
                          className="text-slate-400"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full bg-amber-600 hover:bg-amber-500/100"
                      onClick={() => setReviewingMC(mc.id)}
                    >
                      <Eye className="w-4 h-4 mr-2" /> Review MC
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Bulk Credits Tab */}
      {activeTab === "bulk" && (
        <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
          <h4 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Bulk Credit Tool (Rate Changes / Refunds)
          </h4>
          <p className="text-sm text-slate-400 mb-6">
            Use this tool to issue monetary credits to multiple players at once. Common use cases:
            rate reductions, partial refunds, or compensation.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Target Players</label>
              <div className="flex gap-2 mb-2">
                <Button
                  size="sm"
                  variant={bulkForm.target_type === "term" ? "default" : "outline"}
                  onClick={() => setBulkForm({...bulkForm, target_type: "term", team_id: ""})}
                  className={bulkForm.target_type === "term" ? "bg-yellow-500/100 text-black" : "border-slate-600 text-slate-300"}
                >
                  By Term
                </Button>
                <Button
                  size="sm"
                  variant={bulkForm.target_type === "team" ? "default" : "outline"}
                  onClick={() => setBulkForm({...bulkForm, target_type: "team", term_id: ""})}
                  className={bulkForm.target_type === "team" ? "bg-yellow-500/100 text-black" : "border-slate-600 text-slate-300"}
                >
                  By Team
                </Button>
              </div>

              {bulkForm.target_type === "term" && (
                <Select value={bulkForm.term_id} onValueChange={(v) => setBulkForm({...bulkForm, term_id: v})}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Select term..." />
                  </SelectTrigger>
                  <SelectContent>
                    {terms.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {bulkForm.target_type === "team" && (
                <Select value={bulkForm.team_id} onValueChange={(v) => setBulkForm({...bulkForm, team_id: v})}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Select team..." />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Credit Amount ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 20.00"
                  value={bulkForm.credit_amount}
                  onChange={(e) => setBulkForm({...bulkForm, credit_amount: e.target.value})}
                  className="bg-slate-800/50 border-slate-700 text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Expires In</label>
                <Select value={String(bulkForm.expires_months)} onValueChange={(v) => setBulkForm({...bulkForm, expires_months: parseInt(v)})}>
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 Months</SelectItem>
                    <SelectItem value="6">6 Months</SelectItem>
                    <SelectItem value="12">12 Months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Reason</label>
              <Select value={bulkForm.reason} onValueChange={(v) => setBulkForm({...bulkForm, reason: v})}>
                <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rate_change">Rate Change</SelectItem>
                  <SelectItem value="refund">Partial Refund</SelectItem>
                  <SelectItem value="compensation">Compensation</SelectItem>
                  <SelectItem value="promotion">Promotion</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Reason Details *</label>
              <Textarea
                placeholder="e.g., Rate reduced from $47 to $45 per session for Term 2"
                value={bulkForm.reason_details}
                onChange={(e) => setBulkForm({...bulkForm, reason_details: e.target.value})}
                className="bg-slate-800/50 border-slate-700 text-white"
                rows={3}
              />
            </div>

            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-400 text-sm flex items-center gap-2">
                <Shield className="w-4 h-4" />
                <strong>Important:</strong> This will send a notification to each parent and cannot be undone.
              </p>
            </div>

            <Button
              onClick={bulkGrantCredits}
              disabled={bulkGranting}
              className="w-full bg-blue-600 hover:bg-blue-500/100"
            >
              {bulkGranting ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Issue Bulk Credits
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// Broadcast Announcement Component
function BroadcastAnnouncement() {
  const [recipientType, setRecipientType] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [sendTelegram, setSendTelegram] = useState(true);
  const [sendInapp, setSendInapp] = useState(true);
  const [sending, setSending] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    fetchRecipientOptions();
    fetchHistory();
  }, []);

  const fetchRecipientOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/broadcast/recipient-options`, { withCredentials: true });
      setRecipientOptions(response.data);
    } catch (error) {
      console.error("Error fetching recipient options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/broadcast/history`, { withCredentials: true });
      setHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching broadcast history:", error);
    }
  };

  const sendBroadcast = async () => {
    if (!recipientType) {
      toast.error("Please select recipients");
      return;
    }
    if (!title.trim() || !message.trim()) {
      toast.error("Please enter title and message");
      return;
    }
    if (!sendEmail && !sendTelegram && !sendInapp) {
      toast.error("Please select at least one delivery method");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/admin/broadcast`, {
        recipient_type: recipientType,
        recipient_id: recipientId || null,
        title: title.trim(),
        message: message.trim(),
        send_email: sendEmail,
        send_telegram: sendTelegram,
        send_inapp: sendInapp
      }, { withCredentials: true });

      toast.success(`Broadcast sent! ${response.data.emails_sent} emails, ${response.data.telegram_sent} Telegram, ${response.data.notifications_created} in-app`);
      
      // Reset form
      setTitle("");
      setMessage("");
      setRecipientType("");
      setRecipientId("");
      fetchHistory();
    } catch (error) {
      console.error("Error sending broadcast:", error);
      toast.error(error.response?.data?.detail || "Failed to send broadcast");
    } finally {
      setSending(false);
    }
  };

  const recipientGroups = [
    { group: "Everyone", options: [
      { value: "all", label: "🌐 All Users (Section + Academy)" }
    ]},
    { group: "Section (Adult Teams)", options: [
      { value: "section_all", label: "👥 All Section Members" },
      { value: "captains", label: "🎖️ Team Captains Only" },
      { value: "committee", label: "🏛️ Committee Members" },
      ...(recipientOptions?.section_teams?.map(t => ({ 
        value: "section_team", 
        subId: t.id, 
        label: `⚽ Team: ${t.name}` 
      })) || [])
    ]},
    { group: "Academy", options: [
      { value: "academy_all", label: "👨‍👩‍👧 All Academy Parents" },
      { value: "academy_coaches", label: "🧑‍🏫 Academy Coaches" },
      ...(recipientOptions?.academy_age_groups?.map(ag => ({ 
        value: "academy_age_group", 
        subId: ag, 
        label: `🎯 Age Group: ${ag}` 
      })) || []),
      ...(recipientOptions?.academy_sessions?.map(s => ({ 
        value: "academy_session", 
        subId: s.id, 
        label: `📅 Session: ${s.display_name}` 
      })) || [])
    ]}
  ];

  return (
    <div className="space-y-6">
      {/* Broadcast Form */}
      <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
            <Megaphone className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h4 className="font-bold text-xl text-white">Send Broadcast</h4>
            <p className="text-sm text-slate-400">Send announcements to specific groups via Email, Telegram, and In-app</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Recipient Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Send To *</label>
            <Select 
              value={recipientType + (recipientId ? `::${recipientId}` : "")} 
              onValueChange={(val) => {
                const [type, id] = val.split("::");
                setRecipientType(type);
                setRecipientId(id || "");
              }}
            >
              <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white" data-testid="recipient-select">
                <SelectValue placeholder="Select recipients..." />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                {recipientGroups.map((group) => (
                  <div key={group.group}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-400 bg-slate-800">{group.group}</div>
                    {group.options.map((opt, idx) => (
                      <SelectItem 
                        key={`${opt.value}-${opt.subId || idx}`} 
                        value={opt.subId ? `${opt.value}::${opt.subId}` : opt.value}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Subject / Title *</label>
            <Input
              placeholder="e.g., Training Cancelled Tomorrow"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400"
              data-testid="broadcast-title"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Message *</label>
            <Textarea
              placeholder="Type your announcement message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400 resize-none"
              data-testid="broadcast-message"
            />
          </div>

          {/* Delivery Methods */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">Delivery Methods</label>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={sendEmail} 
                  onCheckedChange={setSendEmail}
                  className="border-slate-600 data-[state=checked]:bg-blue-500/100"
                />
                <Mail className="w-4 h-4 text-blue-400" />
                <span className="text-slate-300">Email</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={sendTelegram} 
                  onCheckedChange={setSendTelegram}
                  className="border-slate-600 data-[state=checked]:bg-sky-500"
                />
                <MessageSquare className="w-4 h-4 text-sky-400" />
                <span className="text-slate-300">Telegram</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox 
                  checked={sendInapp} 
                  onCheckedChange={setSendInapp}
                  className="border-slate-600 data-[state=checked]:bg-emerald-500/100"
                />
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-slate-300">In-App</span>
              </label>
            </div>
          </div>

          {/* Send Button */}
          <Button
            onClick={sendBroadcast}
            disabled={sending || !recipientType || !title.trim() || !message.trim()}
            className="w-full bg-purple-600 hover:bg-purple-500/100 text-white font-semibold py-3"
            data-testid="send-broadcast-btn"
          >
            {sending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Broadcast
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Broadcast History */}
      <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-lg text-white">Recent Broadcasts</h4>
          <Button variant="ghost" size="sm" onClick={fetchHistory} className="text-slate-400 hover:text-white">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {history.length === 0 ? (
          <p className="text-slate-400 text-center py-6">No broadcasts sent yet</p>
        ) : (
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {history.slice(0, 10).map((item) => (
              <div key={item.id} className="p-3 bg-slate-800/50 rounded-lg border border-white/5">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-white">{item.title}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      To: {item.recipient_label} • {item.recipient_count} recipients
                    </p>
                  </div>
                  <div className="text-right text-xs text-slate-400">
                    {new Date(item.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-3 mt-2 text-xs">
                  {item.send_email && <span className="text-blue-400">📧 Email</span>}
                  {item.send_telegram && <span className="text-sky-400">💬 Telegram</span>}
                  {item.send_inapp && <span className="text-emerald-400">🔔 In-app</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// Invoice Management Component
function InvoiceManagement() {
  const [invoices, setInvoices] = useState([]);
  const [paymentsNeedingInvoices, setPaymentsNeedingInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [resendingId, setResendingId] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);
  const [backdatingId, setBackdatingId] = useState(null);
  const [showBackdateModal, setShowBackdateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [backdateDate, setBackdateDate] = useState("");
  const [backdateResend, setBackdateResend] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/academy/admin/invoices/all`, { withCredentials: true });
      setInvoices(response.data.invoices || []);
      setPaymentsNeedingInvoices(response.data.payments_without_invoices || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const resendInvoice = async (invoiceId, parentEmail) => {
    setResendingId(invoiceId);
    try {
      await axios.post(`${API}/academy/admin/invoices/resend/${invoiceId}`, {}, { withCredentials: true });
      toast.success(`Invoice resent to ${parentEmail}`);
      fetchInvoices(); // Refresh to update resent count
    } catch (error) {
      console.error("Error resending invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to resend invoice");
    } finally {
      setResendingId(null);
    }
  };

  const openBackdateModal = (invoice) => {
    setSelectedInvoice(invoice);
    // Default to current invoice date or today
    const currentDate = invoice.invoice_date || invoice.created_at;
    setBackdateDate(currentDate ? currentDate.split('T')[0] : new Date().toISOString().split('T')[0]);
    setBackdateResend(true);
    setShowBackdateModal(true);
  };

  const backdateInvoice = async () => {
    if (!selectedInvoice || !backdateDate) return;
    
    setBackdatingId(selectedInvoice.id);
    try {
      const response = await axios.post(
        `${API}/academy/admin/invoices/backdate/${selectedInvoice.id}`,
        { new_date: backdateDate, resend: backdateResend },
        { withCredentials: true }
      );
      toast.success(response.data.message);
      setShowBackdateModal(false);
      setSelectedInvoice(null);
      fetchInvoices(); // Refresh list
    } catch (error) {
      console.error("Error backdating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to backdate invoice");
    } finally {
      setBackdatingId(null);
    }
  };

  const generateInvoice = async (paymentId, parentEmail) => {
    setGeneratingId(paymentId);
    try {
      const response = await axios.post(`${API}/academy/admin/invoices/generate-for-payment/${paymentId}`, {}, { withCredentials: true });
      toast.success(`Invoice ${response.data.invoice_number} generated and sent to ${parentEmail}`);
      fetchInvoices(); // Refresh list
    } catch (error) {
      console.error("Error generating invoice:", error);
      toast.error(error.response?.data?.detail || "Failed to generate invoice");
    } finally {
      setGeneratingId(null);
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    !searchTerm || 
    inv.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.parent_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredPayments = paymentsNeedingInvoices.filter(p => 
    !searchTerm || 
    p.parent_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.parent_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, email, or invoice number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400"
            data-testid="invoice-search-input"
          />
        </div>
        <Button
          onClick={fetchInvoices}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:bg-slate-800"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Payments Needing Invoices */}
      {filteredPayments.length > 0 && (
        <Card className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-amber-500/20 rounded-full">
              <FileText className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h4 className="font-bold text-white">Payments Without Invoices ({filteredPayments.length})</h4>
              <p className="text-sm text-slate-400">These paid payments need invoices generated</p>
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-semibold text-slate-300">Parent</th>
                  <th className="text-left p-2 font-semibold text-slate-300 hidden md:table-cell">Email</th>
                  <th className="text-right p-2 font-semibold text-slate-300">Amount</th>
                  <th className="text-left p-2 font-semibold text-slate-300 hidden md:table-cell">Paid</th>
                  <th className="text-right p-2 font-semibold text-slate-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="border-b border-white/5 hover:bg-slate-800/30">
                    <td className="p-2 text-white font-medium">{payment.parent_name || 'N/A'}</td>
                    <td className="p-2 text-slate-300 hidden md:table-cell">{payment.parent_email || 'N/A'}</td>
                    <td className="p-2 text-right text-emerald-400 font-semibold">${payment.amount?.toFixed(2) || '0.00'}</td>
                    <td className="p-2 text-slate-400 hidden md:table-cell">
                      {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-2 text-right">
                      <Button
                        onClick={() => generateInvoice(payment.id, payment.parent_email)}
                        disabled={generatingId === payment.id}
                        size="sm"
                        className="bg-amber-500/100 hover:bg-amber-400 text-black text-xs"
                        data-testid={`generate-invoice-${payment.id}`}
                      >
                        {generatingId === payment.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <FileText className="w-3 h-3 mr-1" />
                        )}
                        Generate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* All Invoices */}
      <Card className="p-4 bg-slate-900/50 border border-white/5 rounded-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-emerald-500/20 rounded-full">
            <Receipt className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h4 className="font-bold text-white">All Invoices ({filteredInvoices.length})</h4>
            <p className="text-sm text-slate-400">View and resend payment receipts</p>
          </div>
        </div>

        {filteredInvoices.length === 0 ? (
          <p className="text-slate-400 text-center py-8">No invoices found</p>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-semibold text-slate-300">Invoice #</th>
                  <th className="text-left p-2 font-semibold text-slate-300">Parent</th>
                  <th className="text-left p-2 font-semibold text-slate-300 hidden lg:table-cell">Email</th>
                  <th className="text-right p-2 font-semibold text-slate-300">Amount</th>
                  <th className="text-left p-2 font-semibold text-slate-300 hidden md:table-cell">Date</th>
                  <th className="text-center p-2 font-semibold text-slate-300 hidden md:table-cell">Resent</th>
                  <th className="text-right p-2 font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-white/5 hover:bg-slate-800/30">
                    <td className="p-2">
                      <span className="text-yellow-400 font-mono text-xs">{invoice.invoice_number || invoice.id?.slice(0, 12)}</span>
                    </td>
                    <td className="p-2 text-white">{invoice.parent_name || 'N/A'}</td>
                    <td className="p-2 text-slate-300 hidden lg:table-cell">{invoice.parent_email || 'N/A'}</td>
                    <td className="p-2 text-right text-emerald-400 font-semibold">${invoice.amount?.toFixed(2) || '0.00'}</td>
                    <td className="p-2 text-slate-400 hidden md:table-cell">
                      {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="p-2 text-center hidden md:table-cell">
                      {invoice.resent_history?.length > 0 ? (
                        <span className="text-xs text-blue-400" title={`Resent ${invoice.resent_history.length} time(s)`}>
                          {invoice.resent_history.length}x
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </td>
                    <td className="p-2 text-right flex gap-1 justify-end">
                      <Button
                        onClick={() => openBackdateModal(invoice)}
                        disabled={backdatingId === invoice.id}
                        size="sm"
                        variant="outline"
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 text-xs"
                        data-testid={`backdate-invoice-${invoice.id}`}
                        title="Change invoice date"
                      >
                        {backdatingId === invoice.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Calendar className="w-3 h-3 mr-1" />
                            Backdate
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => resendInvoice(invoice.id, invoice.parent_email)}
                        disabled={resendingId === invoice.id}
                        size="sm"
                        variant="outline"
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs"
                        data-testid={`resend-invoice-${invoice.id}`}
                      >
                        {resendingId === invoice.id ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Send className="w-3 h-3 mr-1" />
                            Resend
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Backdate Modal */}
      {showBackdateModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-white/10 rounded-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              Backdate Invoice
            </h3>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-400 mb-2">Invoice: <span className="text-yellow-400 font-mono">{selectedInvoice.invoice_number || selectedInvoice.id?.slice(0, 12)}</span></p>
                <p className="text-sm text-slate-400">Parent: <span className="text-white">{selectedInvoice.parent_name}</span></p>
                <p className="text-sm text-slate-400">Amount: <span className="text-emerald-400">${selectedInvoice.amount?.toFixed(2)}</span></p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">New Invoice Date</label>
                <Input
                  type="date"
                  value={backdateDate}
                  onChange={(e) => setBackdateDate(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white"
                  data-testid="backdate-date-input"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="backdate-resend"
                  checked={backdateResend}
                  onCheckedChange={setBackdateResend}
                />
                <label htmlFor="backdate-resend" className="text-sm text-slate-300 cursor-pointer">
                  Resend invoice with new date to parent
                </label>
              </div>
              
              {selectedInvoice.backdated && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-xs text-amber-400">
                    ⚠️ This invoice was previously backdated from {selectedInvoice.original_date ? new Date(selectedInvoice.original_date).toLocaleDateString() : 'unknown date'}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBackdateModal(false);
                  setSelectedInvoice(null);
                }}
                className="flex-1 border-slate-600 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={backdateInvoice}
                disabled={!backdateDate || backdatingId}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="confirm-backdate-btn"
              >
                {backdatingId ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Calendar className="w-4 h-4 mr-2" />
                )}
                {backdateResend ? "Backdate & Resend" : "Backdate"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== VENUE MANAGEMENT COMPONENT ==========
function VenueManagement() {
  const [venues, setVenues] = useState([]);
  const [neaAreas, setNeaAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [newVenue, setNewVenue] = useState({
    name: "",
    address: "",
    nea_area: "",
    surface_type: "grass",
    has_lights: false,
    has_shelter: false,
    notes: ""
  });

  useEffect(() => {
    fetchVenues();
    fetchNeaAreas();
  }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/venues`, { withCredentials: true });
      setVenues(response.data.venues || []);
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Failed to load venues");
    } finally {
      setLoading(false);
    }
  };

  const fetchNeaAreas = async () => {
    try {
      const response = await axios.get(`${API}/venues/areas`, { withCredentials: true });
      setNeaAreas(response.data.areas || []);
    } catch (error) {
      console.error("Error fetching NEA areas:", error);
    }
  };

  const createVenue = async () => {
    if (!newVenue.name || !newVenue.nea_area) {
      toast.error("Name and NEA Area are required");
      return;
    }
    try {
      await axios.post(`${API}/venues`, newVenue, { withCredentials: true });
      toast.success("Venue created successfully");
      setShowAddModal(false);
      setNewVenue({ name: "", address: "", nea_area: "", surface_type: "grass", has_lights: false, has_shelter: false, notes: "" });
      fetchVenues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create venue");
    }
  };

  const updateVenue = async () => {
    if (!selectedVenue) return;
    try {
      await axios.put(`${API}/venues/${selectedVenue.id}`, {
        name: selectedVenue.name,
        address: selectedVenue.address,
        nea_area: selectedVenue.nea_area,
        surface_type: selectedVenue.surface_type,
        has_lights: selectedVenue.has_lights,
        has_shelter: selectedVenue.has_shelter,
        notes: selectedVenue.notes
      }, { withCredentials: true });
      toast.success("Venue updated");
      setShowEditModal(false);
      setSelectedVenue(null);
      fetchVenues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update venue");
    }
  };

  const deleteVenue = async (venue) => {
    if (!window.confirm(`Delete "${venue.name}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/venues/${venue.id}`, { withCredentials: true });
      toast.success("Venue deleted");
      fetchVenues();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete venue");
    }
  };

  const getGoogleMapsUrl = (address) => {
    if (!address) return null;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address + ", Singapore")}`;
  };

  const getWazeUrl = (address) => {
    if (!address) return null;
    return `https://waze.com/ul?q=${encodeURIComponent(address + ", Singapore")}&navigate=yes`;
  };

  const filteredVenues = venues.filter(v => 
    v.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.nea_area?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">Venue Management</h3>
          <p className="text-sm text-slate-400">Manage venues and NEA weather area mappings</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-yellow-500 hover:bg-yellow-400 text-black">
          <Plus className="w-4 h-4 mr-2" />
          Add Venue
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search venues by name, address, or area..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800/50 border-slate-700"
        />
      </div>

      {/* Venues List */}
      {loading ? (
        <div className="text-center py-8 text-slate-400">Loading venues...</div>
      ) : filteredVenues.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          {searchTerm ? "No venues match your search" : "No venues yet. Add your first venue!"}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredVenues.map((venue) => (
            <div key={venue.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-white">{venue.name}</h4>
                    <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                      {venue.nea_area}
                    </span>
                    {venue.has_lights && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded">💡 Lights</span>
                    )}
                    {venue.has_shelter && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">🏠 Shelter</span>
                    )}
                  </div>
                  {venue.address && (
                    <p className="text-sm text-slate-400 mt-1">{venue.address}</p>
                  )}
                  {venue.surface_type && (
                    <p className="text-xs text-slate-500 mt-1">Surface: {venue.surface_type}</p>
                  )}
                  
                  {/* Directions Links */}
                  {venue.address && (
                    <div className="flex gap-2 mt-2">
                      <a 
                        href={getGoogleMapsUrl(venue.address)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs flex items-center gap-1 text-blue-400 hover:text-blue-300"
                      >
                        <Navigation className="w-3 h-3" />
                        Google Maps
                      </a>
                      <a 
                        href={getWazeUrl(venue.address)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs flex items-center gap-1 text-purple-400 hover:text-purple-300"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Waze
                      </a>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-slate-600"
                    onClick={() => { setSelectedVenue({...venue}); setShowEditModal(true); }}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => deleteVenue(venue)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Venue Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Venue</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Venue Name *</label>
              <Input
                value={newVenue.name}
                onChange={(e) => setNewVenue({...newVenue, name: e.target.value})}
                placeholder="e.g., SCC Padang"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Address (for directions)</label>
              <Input
                value={newVenue.address}
                onChange={(e) => setNewVenue({...newVenue, address: e.target.value})}
                placeholder="e.g., Connaught Drive, Singapore"
              />
              <p className="text-xs text-slate-400 mt-1">Used for Google Maps/Waze directions</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">NEA Weather Area *</label>
              <select
                value={newVenue.nea_area}
                onChange={(e) => setNewVenue({...newVenue, nea_area: e.target.value})}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2"
              >
                <option value="">-- Select Area --</option>
                {neaAreas.map((area) => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-1">Weather alerts will use this area's forecast</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Surface Type</label>
              <select
                value={newVenue.surface_type}
                onChange={(e) => setNewVenue({...newVenue, surface_type: e.target.value})}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2"
              >
                <option value="grass">Grass</option>
                <option value="artificial">Artificial Turf</option>
                <option value="indoor">Indoor</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newVenue.has_lights}
                  onChange={(e) => setNewVenue({...newVenue, has_lights: e.target.checked})}
                  className="rounded"
                />
                Has Floodlights
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={newVenue.has_shelter}
                  onChange={(e) => setNewVenue({...newVenue, has_shelter: e.target.checked})}
                  className="rounded"
                />
                Has Shelter
              </label>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Notes</label>
              <Input
                value={newVenue.notes}
                onChange={(e) => setNewVenue({...newVenue, notes: e.target.value})}
                placeholder="e.g., Parking available nearby"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button onClick={createVenue} className="bg-yellow-500 hover:bg-yellow-400 text-black">
                <Plus className="w-4 h-4 mr-2" />
                Add Venue
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Venue Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Venue</DialogTitle>
          </DialogHeader>
          {selectedVenue && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Venue Name</label>
                <Input
                  value={selectedVenue.name}
                  onChange={(e) => setSelectedVenue({...selectedVenue, name: e.target.value})}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Address</label>
                <Input
                  value={selectedVenue.address || ""}
                  onChange={(e) => setSelectedVenue({...selectedVenue, address: e.target.value})}
                  placeholder="Full address for directions"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">NEA Weather Area</label>
                <select
                  value={selectedVenue.nea_area}
                  onChange={(e) => setSelectedVenue({...selectedVenue, nea_area: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2"
                >
                  {neaAreas.map((area) => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Surface Type</label>
                <select
                  value={selectedVenue.surface_type || "grass"}
                  onChange={(e) => setSelectedVenue({...selectedVenue, surface_type: e.target.value})}
                  className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2"
                >
                  <option value="grass">Grass</option>
                  <option value="artificial">Artificial Turf</option>
                  <option value="indoor">Indoor</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedVenue.has_lights || false}
                    onChange={(e) => setSelectedVenue({...selectedVenue, has_lights: e.target.checked})}
                    className="rounded"
                  />
                  Has Floodlights
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedVenue.has_shelter || false}
                    onChange={(e) => setSelectedVenue({...selectedVenue, has_shelter: e.target.checked})}
                    className="rounded"
                  />
                  Has Shelter
                </label>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notes</label>
                <Input
                  value={selectedVenue.notes || ""}
                  onChange={(e) => setSelectedVenue({...selectedVenue, notes: e.target.value})}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
                <Button onClick={updateVenue} className="bg-yellow-500 hover:bg-yellow-400 text-black">
                  <Check className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminPanel({ user, logout }) {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "users";
  
  // Role-based access control
  const isAcademyAdmin = user?.role === "academy_admin";
  const isFullAdmin = user?.role === "convenor" || user?.role === "captain";
  const canAccessSection = isFullAdmin; // Only full admins can access section features
  const canAccessAcademy = isFullAdmin || isAcademyAdmin; // Both can access academy
  
  // Admin panel view mode - Section or Academy
  const [adminView, setAdminView] = useState(isAcademyAdmin ? "academy" : "section");
  const [activeTab, setActiveTab] = useState(isAcademyAdmin ? "payments" : defaultTab);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState(null);
  
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTeamName, setNewTeamName] = useState("");
  const [pendingEmailCount, setPendingEmailCount] = useState(0);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [pendingUsersList, setPendingUsersList] = useState([]);
  const [showPendingList, setShowPendingList] = useState(false);
  const [sendingToUser, setSendingToUser] = useState(null);
  const [runningDataFix, setRunningDataFix] = useState(false);
  // Fee-breakdown drift tile counter. Kept inside AdminPanel because the
  // JSX that uses it lives here — an earlier commit accidentally declared
  // it inside CreditManagement, which caused a ReferenceError crash
  // (Aug 2026 fix).
  const [feeMismatchCount, setFeeMismatchCount] = useState(0);
  const [dataFixResult, setDataFixResult] = useState(null);
  const [buildingTracker, setBuildingTracker] = useState(false);
  const [trackerResult, setTrackerResult] = useState(null);
  const [runningAcademyMigration, setRunningAcademyMigration] = useState(false);
  const [academyMigrationResult, setAcademyMigrationResult] = useState(null);
  const [academyMigrationMode, setAcademyMigrationMode] = useState("dry_run"); // dry_run or live
  
  // Account Merge state
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [mergeSourceUser, setMergeSourceUser] = useState(null);
  const [mergeTargetUser, setMergeTargetUser] = useState(null);
  const [mergeUseSourceEmail, setMergeUseSourceEmail] = useState(true);
  const [mergeUseSourceName, setMergeUseSourceName] = useState(false);
  const [mergingAccounts, setMergingAccounts] = useState(false);
  const [potentialDuplicates, setPotentialDuplicates] = useState([]);
  const [loadingDuplicates, setLoadingDuplicates] = useState(false);
  
  // Attendance Import state
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [attendancePreview, setAttendancePreview] = useState(null);
  const [importingAttendance, setImportingAttendance] = useState(false);
  const [previewingAttendance, setPreviewingAttendance] = useState(false);
  const [attendanceImportResult, setAttendanceImportResult] = useState(null);
  const [updatePaymentOnImport, setUpdatePaymentOnImport] = useState(false);
  const [updateBalanceOnImport, setUpdateBalanceOnImport] = useState(true);
  
  // Search and filter state
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSortBy, setUserSortBy] = useState("name");
  const [teamSearchTerm, setTeamSearchTerm] = useState("");
  
  // Edit user dialog
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ 
    name: "", 
    email: "", 
    phone: "", 
    membership_number: "",
    role: "", 
    team_id: "__none__" 
  });
  
  // Edit team dialog
  const [editingTeam, setEditingTeam] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTeams();
    fetchPendingEmailCount();
    fetchFeeMismatchCount();
  }, []);

  const fetchFeeMismatchCount = async () => {
    try {
      const r = await axios.get(`${API}/academy/admin/fee-mismatches`, { withCredentials: true });
      setFeeMismatchCount(r.data?.count || 0);
    } catch (err) {
      // silent — tile just hides the badge
      // eslint-disable-next-line no-console
      console.debug("fee-mismatch count fetch skipped", err?.response?.status);
    }
  };

  // Get filtered and sorted users
  const getFilteredUsers = () => {
    let filtered = users.filter(u => u.is_authorized);
    
    // Search filter
    if (userSearchTerm) {
      const term = userSearchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.role?.toLowerCase().includes(term) ||
        u.membership_number?.toLowerCase().includes(term)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      if (userSortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (userSortBy === "role") return (a.role || "").localeCompare(b.role || "");
      if (userSortBy === "team") {
        const teamA = teams.find(t => t.id === (a.team_id || a.primary_team))?.name || "";
        const teamB = teams.find(t => t.id === (b.team_id || b.primary_team))?.name || "";
        return teamA.localeCompare(teamB);
      }
      return 0;
    });
    
    return filtered;
  };

  const openEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({
      name: u.name || "",
      email: u.email || "",
      phone: u.phone || "",
      membership_number: u.membership_number || "",
      role: u.role || "member",
      team_id: u.team_id || u.primary_team || "__none__"
    });
  };

  const saveUserEdit = async () => {
    if (!editingUser) return;
    try {
      // Convert __none__ back to empty string for API
      const payload = {
        ...editUserForm,
        team_id: editUserForm.team_id === "__none__" ? "" : editUserForm.team_id
      };
      await axios.put(`${API}/admin/users/${editingUser.id}`, payload, { withCredentials: true });
      toast.success("User updated");
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const openEditTeam = (team) => {
    setEditingTeam(team);
  };

  const deleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Delete team "${teamName}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`, { withCredentials: true });
      toast.success(`Team "${teamName}" deleted`);
      fetchTeams();
    } catch (error) {
      console.error("Error deleting team:", error);
      toast.error(error.response?.data?.detail || "Failed to delete team");
    }
  };

  const fetchPendingEmailCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-welcome-emails`, { withCredentials: true });
      setPendingEmailCount(response.data.pending_count);
    } catch (error) {
      console.error("Error fetching pending email count:", error);
    }
  };

  // Account Merge functions
  const fetchPotentialDuplicates = async () => {
    setLoadingDuplicates(true);
    try {
      const response = await axios.get(`${API}/admin/find-potential-duplicates`, { withCredentials: true });
      setPotentialDuplicates(response.data.potential_duplicates || []);
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      toast.error("Failed to fetch potential duplicates");
    } finally {
      setLoadingDuplicates(false);
    }
  };

  const openMergeDialog = (sourceUser = null, targetUser = null) => {
    setMergeSourceUser(sourceUser);
    setMergeTargetUser(targetUser);
    setMergeUseSourceEmail(true);
    setMergeUseSourceName(false);
    setShowMergeDialog(true);
  };

  const executeMerge = async () => {
    if (!mergeSourceUser || !mergeTargetUser) {
      toast.error("Please select both accounts to merge");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to merge these accounts?\n\nSource (will be DELETED): ${mergeSourceUser.email}\nTarget (will be KEPT): ${mergeTargetUser.email}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setMergingAccounts(true);
    try {
      const response = await axios.post(`${API}/admin/merge-accounts`, {
        source_user_id: mergeSourceUser.id,
        target_user_id: mergeTargetUser.id,
        use_source_email: mergeUseSourceEmail,
        use_source_name: mergeUseSourceName
      }, { withCredentials: true });
      
      toast.success(response.data.message);
      setShowMergeDialog(false);
      setMergeSourceUser(null);
      setMergeTargetUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error merging accounts:", error);
      toast.error(error.response?.data?.detail || "Failed to merge accounts");
    } finally {
      setMergingAccounts(false);
    }
  };

  const fetchPendingUsersList = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-welcome-emails/list`, { withCredentials: true });
      setPendingUsersList(response.data.pending_users);
    } catch (error) {
      console.error("Error fetching pending users list:", error);
      toast.error("Failed to fetch pending users list");
    }
  };

  const sendSingleWelcomeEmail = async (userId, email) => {
    setSendingToUser(userId);
    try {
      await axios.post(`${API}/admin/send-welcome-email-single/${userId}`, {}, { withCredentials: true });
      toast.success(`Email sent to ${email}`);
      // Remove from list
      setPendingUsersList(prev => prev.filter(u => u.id !== userId));
      fetchPendingEmailCount();
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.response?.data?.detail || `Failed to send email to ${email}`);
    } finally {
      setSendingToUser(null);
    }
  };

  const markAsSent = async (userId, email) => {
    try {
      await axios.post(`${API}/admin/mark-welcome-email-sent/${userId}`, {}, { withCredentials: true });
      toast.success(`${email} marked as sent`);
      // Remove from list
      setPendingUsersList(prev => prev.filter(u => u.id !== userId));
      fetchPendingEmailCount();
    } catch (error) {
      console.error("Error marking as sent:", error);
      toast.error("Failed to mark as sent");
    }
  };

  const runDataFix = async () => {
    if (!window.confirm(
      "Run Membership Data Fix?\n\n" +
      "This will:\n" +
      "• Link game signups to correct user records\n" +
      "• Update users missing membership numbers\n" +
      "• Rebuild the membership lookup table\n\n" +
      "This is safe to run multiple times."
    )) {
      return;
    }
    
    setRunningDataFix(true);
    setDataFixResult(null);
    try {
      const response = await axios.post(`${API}/admin/run-data-fix`, {}, { withCredentials: true });
      setDataFixResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error running data fix:", error);
      toast.error(error.response?.data?.detail || "Failed to run data fix");
      setDataFixResult({ success: false, error: error.response?.data?.detail || "Unknown error" });
    } finally {
      setRunningDataFix(false);
    }
  };

  const buildMemberTracker = async () => {
    if (!window.confirm(
      "Build Member Tracker?\n\n" +
      "This will:\n" +
      "• Scan all users, members, and game signups\n" +
      "• Create unified identity records linking all IDs\n" +
      "• Enable automatic membership number resolution\n\n" +
      "This is safe to run multiple times."
    )) {
      return;
    }
    
    setBuildingTracker(true);
    setTrackerResult(null);
    try {
      const response = await axios.post(`${API}/admin/build-member-tracker`, {}, { withCredentials: true });
      setTrackerResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      console.error("Error building member tracker:", error);
      toast.error(error.response?.data?.detail || "Failed to build member tracker");
      setTrackerResult({ success: false, error: error.response?.data?.detail || "Unknown error" });
    } finally {
      setBuildingTracker(false);
    }
  };

  const runAcademyMigration = async (mode) => {
    const modeText = mode === "live" ? "LIVE (apply changes)" : "DRY RUN (preview only)";
    const confirmMsg = mode === "live" 
      ? "⚠️ RUN LIVE MIGRATION?\n\nThis will ACTUALLY UPDATE your production data:\n• Update enrollment records with session balances\n• Link players to parents\n• Auto-assign players to sessions\n\nMake sure you've reviewed the dry run first!"
      : "Run Academy Migration Preview?\n\nThis will analyze your data and show what changes WOULD be made without actually applying them.\n\nSafe to run multiple times.";
    
    if (!window.confirm(confirmMsg)) {
      return;
    }
    
    setRunningAcademyMigration(true);
    setAcademyMigrationResult(null);
    setAcademyMigrationMode(mode);
    
    try {
      const response = await axios.post(
        `${API}/academy/admin/migrate-academy-data?mode=${mode}`, 
        {}, 
        { withCredentials: true }
      );
      setAcademyMigrationResult(response.data);
      if (mode === "live") {
        toast.success("Migration completed successfully!");
      } else {
        toast.info("Dry run complete - review the results below");
      }
    } catch (error) {
      console.error("Error running academy migration:", error);
      toast.error(error.response?.data?.detail || "Failed to run migration");
      setAcademyMigrationResult({ 
        success: false, 
        error: error.response?.data?.detail || "Unknown error" 
      });
    } finally {
      setRunningAcademyMigration(false);
    }
  };

  // Attendance Import Functions
  const handleAttendanceFileChange = (e) => {
    const file = e.target.files[0];
    setAttendanceFile(file);
    setAttendancePreview(null);
    setAttendanceImportResult(null);
  };

  const previewAttendanceImport = async () => {
    if (!attendanceFile) {
      toast.error("Please select a file first");
      return;
    }
    
    setPreviewingAttendance(true);
    setAttendancePreview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', attendanceFile);
      
      const response = await axios.post(
        `${API}/academy/admin/import-attendance/preview`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setAttendancePreview(response.data);
      if (response.data.summary.unmatched_players > 0) {
        toast.warning(`${response.data.summary.unmatched_players} player names not matched`);
      } else {
        toast.success("Preview loaded - all players matched!");
      }
    } catch (error) {
      console.error("Error previewing attendance:", error);
      toast.error(error.response?.data?.detail || "Failed to preview file");
    } finally {
      setPreviewingAttendance(false);
    }
  };

  const executeAttendanceImport = async () => {
    if (!attendanceFile) {
      toast.error("Please select a file first");
      return;
    }
    
    if (!attendancePreview) {
      toast.error("Please preview the file first");
      return;
    }
    
    if (!window.confirm(
      `Import ${attendancePreview.summary.matched_players} attendance records?\n\n` +
      `${attendancePreview.summary.unmatched_players} unmatched players will be skipped.\n\n` +
      `Options:\n` +
      `- Update session balances: ${updateBalanceOnImport ? 'YES' : 'NO'}\n` +
      `- Update payment status: ${updatePaymentOnImport ? 'YES' : 'NO'}`
    )) {
      return;
    }
    
    setImportingAttendance(true);
    setAttendanceImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', attendanceFile);
      formData.append('update_payment_status', updatePaymentOnImport);
      formData.append('update_session_balance', updateBalanceOnImport);
      
      const response = await axios.post(
        `${API}/academy/admin/import-attendance/execute`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setAttendanceImportResult(response.data);
      toast.success(`Imported ${response.data.results.attendance_created} attendance records!`);
      
      // Clear file after successful import
      setAttendanceFile(null);
      setAttendancePreview(null);
      
    } catch (error) {
      console.error("Error importing attendance:", error);
      toast.error(error.response?.data?.detail || "Failed to import attendance");
      setAttendanceImportResult({ 
        success: false, 
        error: error.response?.data?.detail || "Unknown error" 
      });
    } finally {
      setImportingAttendance(false);
    }
  };

  const sendWelcomeEmails = async () => {
    if (!window.confirm(`Send welcome emails to up to 20 members? (${pendingEmailCount} pending total)`)) {
      return;
    }
    
    setSendingEmails(true);
    try {
      const response = await axios.post(`${API}/admin/send-welcome-emails`, {}, { withCredentials: true });
      toast.success(`${response.data.sent} welcome emails sent!`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} emails failed to send`);
      }
      if (response.data.remaining > 0) {
        toast.info(`${response.data.remaining} emails remaining - click again to send more`);
      }
      fetchPendingEmailCount();
      if (showPendingList) {
        fetchPendingUsersList();
      }
    } catch (error) {
      console.error("Error sending welcome emails:", error);
      toast.error("Failed to send welcome emails");
    } finally {
      setSendingEmails(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        withCredentials: true
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
    }
  };

  const authorizeUser = async (userId, role, teamId = null, memberType = "non-section") => {
    try {
      await axios.post(
        `${API}/admin/authorize-user`,
        { user_id: userId, role, team_id: teamId, member_type: memberType },
        { withCredentials: true }
      );
      toast.success("User authorized successfully!");
      
      // Immediately update UI by updating the user in state
      setUsers(prevUsers => prevUsers.map(u => 
        u.id === userId 
          ? { ...u, is_authorized: true, role, team_id: teamId, member_type: memberType }
          : u
      ));
      
      // Also refresh from server to ensure consistency
      await fetchUsers();
    } catch (error) {
      console.error("Error authorizing user:", error);
      toast.error("Failed to authorize user");
    }
  };

  const rejectUser = async (userId) => {
    try {
      // Send user_id in request body, not as query param
      await axios.post(`${API}/admin/reject-user`, { user_id: userId }, { withCredentials: true });
      toast.success("User registration rejected");
      
      // Immediately update UI by removing the user from state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
      
      // Also refresh from server to ensure consistency
      await fetchUsers();
    } catch (error) {
      console.error("Error rejecting user:", error);
      toast.error(error.response?.data?.detail || "Failed to reject user");
    }
  };

  const deleteMember = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${userName}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/members/${userId}`, { withCredentials: true });
      toast.success(`${userName} has been deleted`);
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
    } catch (error) {
      console.error("Error deleting member:", error);
      toast.error(error.response?.data?.detail || "Failed to delete member");
    }
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) {
      toast.error("Please enter a team name");
      return;
    }

    try {
      await axios.post(
        `${API}/teams`,
        { name: newTeamName },
        { withCredentials: true }
      );
      toast.success("Team created successfully!");
      setNewTeamName("");
      fetchTeams();
    } catch (error) {
      console.error("Error creating team:", error);
      toast.error("Failed to create team");
    }
  };

  const handleRecomputeAgeGroups = async () => {
    if (recomputing) return;
    if (!window.confirm("Recompute every child's true age group and place unassigned children into bucket teams? This is safe to run repeatedly (idempotent).")) {
      return;
    }
    setRecomputing(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/recompute-age-groups-and-place`,
        { dry_run: false },
        { withCredentials: true }
      );
      setRecomputeResult(res.data);
      const d = res.data || {};
      toast.success(
        `Done — ${d.age_group_updated ?? 0} age-groups updated, ${d.placed ?? 0} placed (${d.played_up ?? 0} playing up), ${d.unplaced_count ?? 0} unplaced.`,
        { duration: 6000 }
      );
    } catch (error) {
      console.error("Error recomputing age-groups:", error);
      toast.error(error.response?.data?.detail || "Failed to recompute age-groups");
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5" data-testid="back-button">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-wide font-display">SCC Football</h1>
          </div>
          <EnvironmentBanner />
        </div>
      </header>

      {/* Recompute age-groups result dialog */}
      <Dialog open={!!recomputeResult} onOpenChange={(o) => { if (!o) setRecomputeResult(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg" data-testid="recompute-result-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-emerald-400" />
              Age-Groups Recomputed &amp; Placed
            </DialogTitle>
          </DialogHeader>
          {recomputeResult && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Scanned", recomputeResult.scanned],
                  ["Age-groups updated", recomputeResult.age_group_updated],
                  ["Placed into teams", recomputeResult.placed],
                  ["Playing up", recomputeResult.played_up],
                  ["Already had a team", recomputeResult.already_had_team],
                  ["Unplaced", recomputeResult.unplaced_count],
                ].map(([label, val]) => (
                  <div key={label} className="p-3 bg-slate-800/60 rounded border border-slate-700">
                    <p className="text-xs text-slate-400">{label}</p>
                    <p className="text-xl font-bold text-white">{val ?? 0}</p>
                  </div>
                ))}
              </div>
              {Array.isArray(recomputeResult.unplaced) && recomputeResult.unplaced.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-300 mb-1">
                    Unplaced children ({recomputeResult.unplaced.length} shown) — need manual handling:
                  </p>
                  <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid="recompute-unplaced-list">
                    {recomputeResult.unplaced.map((u) => (
                      <div key={u.player_id} className="p-2 bg-slate-800/40 rounded border border-slate-700/60 text-xs">
                        <span className="font-medium text-white">{u.name || "(no name)"}</span>
                        <span className="text-slate-400">
                          {" "}— {u.reason === "no_parseable_birthdate"
                            ? "no valid birthdate"
                            : `no bucket at/above ${u.true_age_group || "?"} (${u.gender || "?"})`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {recomputeResult.deleted_e2e_team > 0 && (
                <p className="text-xs text-slate-500">Removed {recomputeResult.deleted_e2e_team} stray test team.</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-display" data-testid="page-title">Admin Panel</h2>
        
        {/* Section/Academy Toggle - Only show for full admins */}
        {canAccessSection && canAccessAcademy && (
          <div className="mb-8">
            <div className="inline-flex bg-slate-800/50 border border-white/10 rounded-lg p-1">
              <button
                onClick={() => { setAdminView("section"); setActiveTab(defaultTab); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                  adminView === "section"
                    ? "bg-yellow-500 text-black"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <Trophy className="w-4 h-4" />
                Section
              </button>
              <button
                onClick={() => { setAdminView("academy"); setActiveTab("payments"); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                  adminView === "academy"
                    ? "bg-blue-500 text-white"
                    : "text-slate-400 hover:text-white"
                }`}
              >
                <GraduationCap className="w-4 h-4" />
                Academy
              </button>
            </div>
          </div>
        )}
        
        {/* Academy Admin Badge */}
        {isAcademyAdmin && (
          <div className="mb-6 inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 px-3 py-1.5 rounded-full text-sm">
            <GraduationCap className="w-4 h-4" />
            Academy Administrator
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 bg-slate-800/50 border border-white/5 flex-wrap">
            {/* Section Tabs - Only shown in Section view */}
            {adminView === "section" && canAccessSection && (
              <>
                <TabsTrigger value="users" data-testid="users-tab" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Users</TabsTrigger>
                <TabsTrigger value="teams" data-testid="teams-tab" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Teams</TabsTrigger>
                <TabsTrigger value="venues" data-testid="venues-tab" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Venues</TabsTrigger>
                <TabsTrigger value="broadcast" data-testid="broadcast-tab" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Broadcast</TabsTrigger>
                <TabsTrigger value="section-tools" data-testid="section-tools-tab" className="data-[state=active]:bg-yellow-500/100 data-[state=active]:text-black">Section Tools</TabsTrigger>
                <TabsTrigger value="tools" data-testid="system-tools-tab" className="data-[state=active]:bg-red-500/100 data-[state=active]:text-white">System Tools</TabsTrigger>
              </>
            )}
            {/* Academy Tabs - Shown in Academy view or for academy admins */}
            {(adminView === "academy" || isAcademyAdmin) && (
              <>
                <TabsTrigger value="payments" data-testid="payments-tab" className="data-[state=active]:bg-blue-500/100 data-[state=active]:text-white">Payments</TabsTrigger>
                <TabsTrigger value="credits" data-testid="credits-tab" className="data-[state=active]:bg-blue-500/100 data-[state=active]:text-white">Credits</TabsTrigger>
                <TabsTrigger value="invoices" data-testid="invoices-tab" className="data-[state=active]:bg-blue-500/100 data-[state=active]:text-white">Invoices</TabsTrigger>
                <TabsTrigger value="academy-tools" data-testid="academy-tools-tab" className="data-[state=active]:bg-blue-500/100 data-[state=active]:text-white">Academy Tools</TabsTrigger>
                {isFullAdmin && (
                  <TabsTrigger value="tools" data-testid="system-tools-tab-academy" className="data-[state=active]:bg-red-500/100 data-[state=active]:text-white">System Tools</TabsTrigger>
                )}
              </>
            )}
          </TabsList>

          <TabsContent value="payments">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                  <h3 className="text-2xl font-bold text-white">Academy Payment Tracking</h3>
                  <p className="text-sm text-slate-400 mt-1">Legacy tracking view · the full command centre is now at <Link to="/admin/payments" className="text-emerald-400 underline">/admin/payments</Link>.</p>
                </div>
                <Link to="/admin/payments" data-testid="open-payments-hub-link">
                  <Button className="bg-emerald-600 hover:bg-emerald-500 text-white">
                    Open Payment Command Centre →
                  </Button>
                </Link>
              </div>
              <PaymentTracking />
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h3 className="text-2xl font-bold text-white">User Management</h3>
                <div className="flex gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search users..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="pl-9 bg-slate-800/50 border-slate-700 text-white"
                      data-testid="user-search-input"
                    />
                  </div>
                  <Select value={userSortBy} onValueChange={setUserSortBy}>
                    <SelectTrigger className="w-[140px] bg-slate-800/50 border-slate-700 text-white">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="name">Name</SelectItem>
                      <SelectItem value="role">Role</SelectItem>
                      <SelectItem value="team">Team</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* Pending Authorizations */}
                {users.filter(u => !u.is_authorized).length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-lg text-amber-400 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Pending Authorizations ({users.filter(u => !u.is_authorized).length})
                    </h4>
                    {users.filter(u => !u.is_authorized).map((u) => (
                      <UserCard
                        key={u.id}
                        user={u}
                        teams={teams}
                        onAuthorize={authorizeUser}
                        onReject={rejectUser}
                      />
                    ))}
                  </div>
                )}

                {/* Authorized Users - Searchable & Sortable */}
                <h4 className="font-semibold text-lg text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-400" />
                  Authorized Users ({getFilteredUsers().length})
                </h4>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left p-3 text-slate-400 font-medium">Name</th>
                        <th className="text-left p-3 text-slate-400 font-medium hidden md:table-cell">Email</th>
                        <th className="text-left p-3 text-slate-400 font-medium">Role</th>
                        <th className="text-left p-3 text-slate-400 font-medium hidden lg:table-cell">Team</th>
                        <th className="text-left p-3 text-slate-400 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredUsers().map((u) => (
                        <tr key={u.id} className="border-b border-slate-800 hover:bg-slate-800/30" data-testid={`user-row-${u.id}`}>
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-white text-sm sm:text-base truncate max-w-[120px] sm:max-w-none">{u.name}</p>
                              {u.membership_number && (
                                <p className="text-xs text-slate-500">#{u.membership_number}</p>
                              )}
                              {/* Show email on mobile below name */}
                              <p className="text-xs text-slate-400 md:hidden truncate max-w-[150px]">{u.email}</p>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-slate-300 hidden md:table-cell">{u.email}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              u.role === 'convenor' ? 'bg-purple-500/20 text-purple-400' :
                              u.role === 'captain' ? 'bg-yellow-500/20 text-yellow-400' :
                              u.role === 'team_captain' ? 'bg-blue-500/20 text-blue-400' :
                              u.role === 'vice_captain' ? 'bg-cyan-500/20 text-cyan-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {u.role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-3 text-sm text-slate-300 hidden lg:table-cell">
                            {u.team_id ? (teams.find(t => t.id === u.team_id)?.name || 'Unknown') : 
                             u.primary_team ? (teams.find(t => t.id === u.primary_team)?.name || '-') : '-'}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditUser(u)}
                                className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 h-8 w-8 p-0"
                                title="Edit user"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {u.id !== user?.id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteMember(u.id, u.name)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {getFilteredUsers().length === 0 && (
                  <p className="text-center text-slate-400 py-8">No users found matching "{userSearchTerm}"</p>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="teams">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h3 className="text-2xl font-bold text-white">Team Management</h3>
                <div className="flex gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search teams..."
                      value={teamSearchTerm}
                      onChange={(e) => setTeamSearchTerm(e.target.value)}
                      className="pl-9 bg-slate-800/50 border-slate-700 text-white"
                      data-testid="team-search-input"
                    />
                  </div>
                </div>
              </div>
              
              {/* Create New Team */}
              <div className="mb-8 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                <h4 className="font-semibold text-lg text-white mb-4">Create New Team</h4>
                <div className="flex gap-4">
                  <Input
                    placeholder="Team name (e.g., Legends, Tigers)"
                    value={newTeamName}
                    onChange={(e) => setNewTeamName(e.target.value)}
                    className="flex-1 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-400"
                    data-testid="new-team-input"
                  />
                  <Button
                    onClick={createTeam}
                    className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                    data-testid="create-team-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Team
                  </Button>
                </div>
              </div>

              {/* Teams List */}
              <div className="space-y-4">
                {teams
                  .filter(t => !teamSearchTerm || t.name?.toLowerCase().includes(teamSearchTerm.toLowerCase()))
                  .map((team) => (
                    <Card key={team.id} className="p-4 bg-slate-800/50 border border-yellow-500/30 rounded-lg" data-testid={`team-card-${team.id}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                            <Shield className="w-7 h-7 text-yellow-500" />
                          </div>
                          <div>
                            <p className="font-bold text-white text-lg">{team.name}</p>
                            <div className="flex items-center gap-3 text-sm text-slate-400">
                              <span>{team.members?.length || 0} members</span>
                              {team.captain_id && (
                                <span className="text-yellow-400">
                                  Captain: {users.find(u => u.id === team.captain_id)?.name || 'Unknown'}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Link to={`/teams/${team.id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                            >
                              <Users className="w-4 h-4 mr-2" />
                              Manage Players
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditTeam(team)}
                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTeam(team.id, team.name)}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                  
                {teams.filter(t => !teamSearchTerm || t.name?.toLowerCase().includes(teamSearchTerm.toLowerCase())).length === 0 && (
                  <p className="text-center text-slate-400 py-8">No teams found</p>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="venues">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <VenueManagement />
            </Card>
          </TabsContent>

          <TabsContent value="broadcast">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">📢 Broadcast Announcements</h3>
              <BroadcastAnnouncement />
            </Card>
          </TabsContent>

          <TabsContent value="credits">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">🎫 Session Credits</h3>
              <CreditManagement />
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Invoice Management</h3>
              <InvoiceManagement />
            </Card>
          </TabsContent>

          {/* Section Tools - Only for section admins in section view */}
          <TabsContent value="section-tools">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Section Admin Tools</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Member Management */}
                <Link to="/member-management">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <UserCog className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">Member Management</h4>
                          <p className="text-sm text-slate-400">Edit roles, suspend/reactivate members</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Account Merge Tool */}
                <Card 
                  className="p-6 bg-slate-800/50 border border-orange-500/30 hover:border-orange-500 rounded-lg transition-all cursor-pointer group"
                  onClick={() => {
                    fetchPotentialDuplicates();
                    openMergeDialog();
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-orange-500/20 rounded-full">
                        <Users className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-white group-hover:text-orange-400 transition-colors">Merge Duplicate Accounts</h4>
                        <p className="text-sm text-slate-400">Combine duplicate member accounts</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-orange-400" />
                  </div>
                </Card>

                {/* User Rights */}
                <Link to="/user-rights">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <UserCog className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">User Rights</h4>
                          <p className="text-sm text-slate-400">Manage permissions & roles</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Merchant Management */}
                <Link to="/admin/merchants">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <Store className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Merchant Management</h4>
                          <p className="text-sm text-slate-400">View & manage registered merchants</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                {/* Media Library */}
                <Link to="/admin/media" data-testid="admin-panel-media-link">
                  <Card className="p-6 bg-slate-800/50 border border-cyan-500/30 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-full">
                          <Image className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">Media Library</h4>
                          <p className="text-sm text-slate-400">Photos & documents in object storage</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>

                {/* Branding Settings */}
                <Link to="/admin/branding-settings">
                  <Card className="p-6 bg-slate-800/50 border border-yellow-500/30 hover:border-yellow-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                          <Palette className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-yellow-400 transition-colors">Branding Settings</h4>
                          <p className="text-sm text-slate-400">Toggle SCC branding on/off</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-yellow-400" />
                    </div>
                  </Card>
                </Link>
              </div>
            </Card>
          </TabsContent>

          {/* Academy Tools */}
          <TabsContent value="academy-tools">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Academy Admin Tools</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Data Management */}
                <Link to="/academy/data-management">
                  <Card className="p-6 bg-slate-800/50 border border-cyan-500/30 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-full">
                          <Database className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">Data Management</h4>
                          <p className="text-sm text-slate-400">Export players, parents, registrations</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>

                {/* Manual Invoice — ad-hoc academy invoicing */}
                <Link to="/admin/manual-invoice" data-testid="admin-manual-invoice-link">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <FileText className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Create Manual Invoice</h4>
                          <p className="text-sm text-slate-400">Ad-hoc charge for a parent — registration fee, kit, tournaments…</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                {/* Reminders Due — admin-reviewed unpaid-invoice reminders */}
                <Link to="/admin/reminders-due" data-testid="admin-reminders-due-link">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <Bell className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Reminders Due</h4>
                          <p className="text-sm text-slate-400">Review overdue unpaid invoices and send friendly reminders</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                {/* Migration Tools (merged into /admin/migrations) */}
                <Link to="/admin/migrations" data-testid="migration-tools-link">
                  <Card className="p-6 bg-slate-800/50 border border-red-500/30 hover:border-red-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full">
                          <Wrench className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-red-400 transition-colors">Migration Tools</h4>
                          <p className="text-sm text-slate-400">One-shot DB cleanups with dry-run support</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Academy Roster */}
                <Link to="/admin/academy-roster">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <Users className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Academy Roster</h4>
                          <p className="text-sm text-slate-400">Players, parents & send info emails</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Registration Approvals */}
                <Link to="/academy/pending-registrations">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <CheckCircle className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Registration Approvals</h4>
                          <p className="text-sm text-slate-400">Approve pending registrations</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                {/* Academy Inbox — Feb 2026: registrations from
                    https://academy.sccfootball.com land in db.external_registrations.
                    Admins review and one-click "Approve & Sync to Portal" which
                    creates the canonical academy_parent_registrations row and
                    emails the parent a set-password link. */}
                <Link to="/admin/academy-inbox" data-testid="admin-panel-academy-inbox-link">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <Inbox className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Academy Inbox</h4>
                          <p className="text-sm text-slate-400">Approve registrations from academy.sccfootball.com & sync to Portal</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                {/* Camps & Tournaments — Feb 2026: unified Events module
                    backed by db.events. Admin creates a camp or tournament
                    here once; the academy site picks it up automatically via
                    GET /api/public/events. Each event has a public reg URL
                    and a downloadable QR code. */}
                <Link to="/admin/events" data-testid="admin-panel-events-link">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <Trophy className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">Camps & Tournaments</h4>
                          <p className="text-sm text-slate-400">Create events that appear on the academy site automatically</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Master QRs — dedicated shortcut so admins don't have to
                    dive into Events first. Master QRs cover camps, tournaments,
                    free trials, and kit ordering — all in one place. */}
                <Link to="/admin/events/master-qrs" data-testid="admin-panel-master-qrs-link">
                  <Card className="p-6 bg-slate-800/50 border border-fuchsia-500/30 hover:border-fuchsia-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-fuchsia-500/20 rounded-full">
                          <QrCode className="w-6 h-6 text-fuchsia-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-fuchsia-400 transition-colors">Master QRs</h4>
                          <p className="text-sm text-slate-400">Reusable QR codes for Camps, Tournaments, Free Trials &amp; Kit Ordering</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-fuchsia-400" />
                    </div>
                  </Card>
                </Link>

                {/* Fee Mismatches — audit trail for the server-side revalidation gate.
                    Only shows a red badge when > 0 rows are flagged. */}
                <Link to="/admin/fee-mismatches" data-testid="admin-panel-fee-mismatches-link">
                  <Card className={`p-6 bg-slate-800/50 border rounded-lg transition-all cursor-pointer group ${
                    feeMismatchCount > 0
                      ? 'border-red-500/60 hover:border-red-500'
                      : 'border-slate-700 hover:border-slate-500'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`flex items-center justify-center w-12 h-12 rounded-full ${
                          feeMismatchCount > 0 ? 'bg-red-500/20' : 'bg-slate-700/40'
                        }`}>
                          <AlertTriangle className={`w-6 h-6 ${feeMismatchCount > 0 ? 'text-red-400' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <h4 className={`font-bold text-lg text-white transition-colors ${
                            feeMismatchCount > 0 ? 'group-hover:text-red-400' : 'group-hover:text-slate-300'
                          }`}>
                            Fee Mismatches
                            {feeMismatchCount > 0 && (
                              <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-red-500 text-white" data-testid="fee-mismatch-badge">
                                {feeMismatchCount}
                              </span>
                            )}
                          </h4>
                          <p className="text-sm text-slate-400">
                            {feeMismatchCount > 0
                              ? "Client-sent totals differed from server-computed. Review each row."
                              : "All fees match server-computed values."}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className={`w-5 h-5 text-slate-600 ${feeMismatchCount > 0 ? 'group-hover:text-red-400' : 'group-hover:text-slate-400'}`} />
                    </div>
                  </Card>
                </Link>

                {/* Term 3 Fee Reconciliation — READ-ONLY audit of charged vs
                    canonical-engine-correct. No writes/refunds; report only. */}
                <Link to="/admin/term3-reconciliation" data-testid="admin-panel-term3-recon-link">
                  <Card className="p-6 bg-slate-800/50 border border-slate-700 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-cyan-500/20">
                          <DollarSign className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">
                            Term 3 Fee Reconciliation
                          </h4>
                          <p className="text-sm text-slate-400">
                            Read-only: charged vs canonical-correct for Term 3 (pre-fix). Export CSV for refunds/credits.
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>


                {/* Duplicate Session Cleanup — scan a term for duplicate
                    session-template rows, dry-run then archive the extras so
                    parents stop seeing the same slot 2–3× in the wizard. */}
                <Link to="/admin/duplicate-sessions" data-testid="admin-panel-duplicate-sessions-link">
                  <Card className="p-6 bg-slate-800/50 border border-cyan-500/30 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-full">
                          <Layers className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">Duplicate Session Cleanup</h4>
                          <p className="text-sm text-slate-400">Scan &amp; safely merge duplicate session slots (dry-run → archive extras)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>


                {/* Duplicate Registrations Sweep — identity-based (parent +
                    children + term). Read-only scan → confirm-cancel per group. */}
                <Link to="/admin/duplicate-registrations" data-testid="admin-panel-dup-registrations-link">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <AlertTriangle className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Duplicate Registrations Sweep</h4>
                          <p className="text-sm text-slate-400">Find & cancel duplicate transactions by parent + children + term (read-only scan → confirm)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                <Link to="/admin/holding-group" data-testid="admin-panel-holding-group-link">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <Users className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">Holding Group</h4>
                          <p className="text-sm text-slate-400">Age-group buckets by term — see who registered &amp; paid, and bulk-assign kids to weekday sessions</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>

                <Link to="/admin/walk-ins" data-testid="admin-panel-walkins-link">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <UserPlus className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">Walk-in Follow-ups</h4>
                          <p className="text-sm text-slate-400">Players who turned up without registering (logged by coaches) — follow up to register &amp; bill</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>

                <Link to="/admin/trials" data-testid="admin-panel-trials-link">
                  <Card className="p-6 bg-slate-800/50 border border-cyan-500/30 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-full">
                          <UserPlus className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">Trials Pipeline</h4>
                          <p className="text-sm text-slate-400">Schedule trials, hand off to coaches (WhatsApp), confirm with parents, track outcomes &amp; surveys</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>


                {/* Recompute age-groups & place — surfaces the admin backfill so
                    nobody needs the browser console. Shows counts + unplaced. */}
                <Card
                  onClick={handleRecomputeAgeGroups}
                  data-testid="admin-panel-recompute-age-groups-card"
                  className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                        <RefreshCw className={`w-6 h-6 text-emerald-400 ${recomputing ? "animate-spin" : ""}`} />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">
                          {recomputing ? "Recomputing…" : "Recompute Age-Groups & Place"}
                        </h4>
                        <p className="text-sm text-slate-400">
                          Recompute every child&apos;s true age group and auto-place unassigned kids into bucket teams
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                  </div>
                </Card>
                
                {/* Term & Session Management */}
                <Link to="/academy/sessions">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <Calendar className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">Term & Sessions</h4>
                          <p className="text-sm text-slate-400">Manage terms and session templates</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* League Fixtures */}
                <Link to="/academy/fixtures" data-testid="admin-panel-fixtures-link">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <Trophy className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">League Games</h4>
                          <p className="text-sm text-slate-400">
                            JSSL and SYL fixtures, matchday squads, results and the league table
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Duplicate players */}
                <Link to="/admin/duplicate-players" data-testid="admin-panel-duplicates-link">
                  <Card className="p-6 bg-slate-800/50 border border-red-500/30 hover:border-red-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full">
                          <Users className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-red-400 transition-colors">Duplicate Players</h4>
                          <p className="text-sm text-slate-400">
                            Find children with more than one record and merge them onto one number
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Discount Codes */}
                <Link to="/academy/discount-codes">
                  <Card className="p-6 bg-slate-800/50 border border-pink-500/30 hover:border-pink-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-pink-500/20 rounded-full">
                          <Tag className="w-6 h-6 text-pink-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-pink-400 transition-colors">Discount Codes</h4>
                          <p className="text-sm text-slate-400">Manage academy discount codes</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-pink-400" />
                    </div>
                  </Card>
                </Link>
                
                {/* Academy Broadcast */}
                <Link to="/academy/broadcast">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <Megaphone className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">Academy Broadcast</h4>
                          <p className="text-sm text-slate-400">Send announcements to parents</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>

                {/* Register for Parent */}
                <Link to="/admin/register-for-parent">
                  <Card className="p-6 bg-slate-800/50 border border-teal-500/30 hover:border-teal-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-teal-500/20 rounded-full">
                          <UserPlus className="w-6 h-6 text-teal-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-teal-400 transition-colors">Register for Parent</h4>
                          <p className="text-sm text-slate-400">Create account & enroll child for non-tech-savvy parents</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-teal-400" />
                    </div>
                  </Card>
                </Link>

                {/* Manage Teams (C8 — May 2026) */}
                <Link to="/admin/academy-teams-management" data-testid="admin-manage-teams-link">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <Users className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Manage Teams</h4>
                          <p className="text-sm text-slate-400">Create, rename, or remove academy teams (U6, U16, U18, etc.)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                <Link to="/admin/kit-management" data-testid="admin-kit-management-link">
                  <Card className="p-6 bg-slate-800/50 border border-fuchsia-500/30 hover:border-fuchsia-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-fuchsia-500/20 rounded-full">
                          <Shirt className="w-6 h-6 text-fuchsia-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-fuchsia-400 transition-colors">Kit Management</h4>
                          <p className="text-sm text-slate-400">Jersey numbers, kit orders, size selection &amp; supplier compilation</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-fuchsia-400" />
                    </div>
                  </Card>
                </Link>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="tools">
            <Card className="p-6 bg-slate-900/50 border border-white/5 rounded-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Admin Tools</h3>
              
              {/* Send Welcome Emails Card - Always visible */}
              <Card className={`p-4 mb-6 border rounded-lg ${pendingEmailCount > 0 ? 'border-yellow-500/50 bg-yellow-500/10' : 'border-emerald-500/30 bg-emerald-500/10'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${pendingEmailCount > 0 ? 'bg-yellow-500/20' : 'bg-emerald-500/20'}`}>
                      <Mail className={`w-5 h-5 ${pendingEmailCount > 0 ? 'text-yellow-500' : 'text-emerald-500'}`} />
                    </div>
                    <div>
                      {pendingEmailCount > 0 ? (
                        <>
                          <p className="font-bold text-white">{pendingEmailCount} members have not logged in yet</p>
                          <p className="text-sm text-slate-400">Send them welcome emails with login instructions</p>
                        </>
                      ) : (
                        <>
                          <p className="font-bold text-white">All members have received welcome emails</p>
                          <p className="text-sm text-slate-400">No pending welcome emails to send</p>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {pendingEmailCount > 0 && (
                      <Button
                        onClick={() => {
                          setShowPendingList(!showPendingList);
                          if (!showPendingList) fetchPendingUsersList();
                        }}
                        variant="outline"
                        className="font-semibold border-slate-600 text-slate-300 hover:bg-slate-800"
                        data-testid="toggle-pending-list-btn"
                      >
                        {showPendingList ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                        {showPendingList ? "Hide List" : "View List"}
                      </Button>
                    )}
                    <Button
                      onClick={sendWelcomeEmails}
                      disabled={sendingEmails || pendingEmailCount === 0}
                      className={`font-semibold ${pendingEmailCount > 0 ? 'bg-yellow-500/100 hover:bg-yellow-400 text-black' : 'bg-slate-700 text-slate-400 cursor-not-allowed'}`}
                      data-testid="send-welcome-emails-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {sendingEmails ? "Sending..." : "Send Batch (20)"}
                    </Button>
                  </div>
                </div>
                
                {/* Pending Users List */}
                {showPendingList && pendingUsersList.length > 0 && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-white">Pending Members ({pendingUsersList.length})</h4>
                      <Button
                        onClick={fetchPendingUsersList}
                        variant="ghost"
                        size="sm"
                        className="text-slate-400 hover:text-white"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                      </Button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-800/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2 font-semibold text-slate-300">Name</th>
                            <th className="text-left p-2 font-semibold text-slate-300">Email</th>
                            <th className="text-left p-2 font-semibold text-slate-300 hidden md:table-cell">Team</th>
                            <th className="text-right p-2 font-semibold text-slate-300">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pendingUsersList.map((pendingUser) => (
                            <tr key={pendingUser.id} className="border-b border-white/5 hover:bg-slate-800/30">
                              <td className="p-2 text-white">{pendingUser.name}</td>
                              <td className="p-2 text-slate-300">{pendingUser.email}</td>
                              <td className="p-2 text-slate-400 hidden md:table-cell">{pendingUser.team_name || '-'}</td>
                              <td className="p-2 text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button
                                    onClick={() => sendSingleWelcomeEmail(pendingUser.id, pendingUser.email)}
                                    disabled={sendingToUser === pendingUser.id}
                                    size="sm"
                                    className="bg-blue-500/100 hover:bg-blue-400 text-white text-xs px-2 py-1"
                                    title="Send welcome email"
                                  >
                                    {sendingToUser === pendingUser.id ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Send className="w-3 h-3" />
                                    )}
                                  </Button>
                                  <Button
                                    onClick={() => markAsSent(pendingUser.id, pendingUser.email)}
                                    size="sm"
                                    variant="outline"
                                    className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10 text-xs px-2 py-1"
                                    title="Mark as sent (skip)"
                                  >
                                    <Check className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      💡 Click <Send className="w-3 h-3 inline" /> to send email, or <Check className="w-3 h-3 inline" /> to mark as sent without sending (for manual handling)
                    </p>
                  </div>
                )}
              </Card>
              
              {/* Data Fix Card - Fix Missing Membership Numbers */}
              <Card className="p-4 mb-6 border rounded-lg border-red-500/30 bg-red-500/10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-red-500/20 rounded-full">
                      <Database className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Fix Missing Membership Numbers</p>
                      <p className="text-sm text-slate-400">Link game signups to correct user records & rebuild lookup table</p>
                    </div>
                  </div>
                  <Button
                    onClick={runDataFix}
                    disabled={runningDataFix}
                    className="bg-red-600 hover:bg-red-500 text-white font-semibold"
                    data-testid="run-data-fix-btn"
                  >
                    {runningDataFix ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Running...
                      </>
                    ) : (
                      <>
                        <Wrench className="w-4 h-4 mr-2" />
                        Run Data Fix
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Data Fix Results */}
                {dataFixResult && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    {dataFixResult.success ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-semibold">Data Fix Completed Successfully!</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Users Updated</p>
                            <p className="text-lg font-bold text-emerald-400">{dataFixResult.results?.step4_users_updated || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Records Created</p>
                            <p className="text-lg font-bold text-blue-400">{dataFixResult.results?.step4_users_created || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Lookup Entries</p>
                            <p className="text-lg font-bold text-yellow-400">{dataFixResult.results?.step5_lookup_entries || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Unfixable</p>
                            <p className="text-lg font-bold text-slate-400">{dataFixResult.results?.step3_cannot_fix || 0}</p>
                          </div>
                        </div>
                        {dataFixResult.results?.unfixable_signups?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-400 mb-1">Unfixable signups (no matching user found):</p>
                            <div className="text-xs text-slate-500 max-h-24 overflow-y-auto">
                              {dataFixResult.results.unfixable_signups.map((u, i) => (
                                <span key={i} className="mr-2">{u.name} ({u.game})</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Error: {dataFixResult.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </Card>
              
              {/* Member Tracker Card */}
              <Card className="p-4 mb-6 border rounded-lg border-blue-500/30 bg-blue-500/10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-full">
                      <Database className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Build Member Tracker</p>
                      <p className="text-sm text-slate-400">Create unified identity linking user_id, member_id, and membership numbers</p>
                    </div>
                  </div>
                  <Button
                    onClick={buildMemberTracker}
                    disabled={buildingTracker}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-semibold"
                    data-testid="build-member-tracker-btn"
                  >
                    {buildingTracker ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Building...
                      </>
                    ) : (
                      <>
                        <Wrench className="w-4 h-4 mr-2" />
                        Build Tracker
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Tracker Results */}
                {trackerResult && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    {trackerResult.success ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="font-semibold">Member Tracker Built Successfully!</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Trackers Created</p>
                            <p className="text-lg font-bold text-emerald-400">{trackerResult.results?.total_trackers || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Users Processed</p>
                            <p className="text-lg font-bold text-blue-400">{trackerResult.results?.users_processed || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Orphans Matched</p>
                            <p className="text-lg font-bold text-yellow-400">{trackerResult.results?.orphans_matched || 0}</p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Still Unlinked</p>
                            <p className="text-lg font-bold text-red-400">{trackerResult.results?.orphans_unmatched || 0}</p>
                          </div>
                        </div>
                        {trackerResult.results?.unmatched_list?.length > 0 && (
                          <div className="mt-3">
                            <p className="text-xs text-slate-400 mb-1">Unlinked signups (need manual linking in Charge Sheet):</p>
                            <div className="text-xs text-slate-500 max-h-24 overflow-y-auto bg-slate-800/30 p-2 rounded">
                              {trackerResult.results.unmatched_list.slice(0, 15).map((u, i) => (
                                <div key={i} className="mb-1">{u.name} - {u.game}</div>
                              ))}
                              {trackerResult.results.unmatched_list.length > 15 && (
                                <div className="text-slate-600">...and {trackerResult.results.unmatched_list.length - 15} more</div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Error: {trackerResult.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </Card>
              
              {/* Academy Data Migration Card */}
              <Card className="p-4 mb-6 border rounded-lg border-yellow-500/30 bg-yellow-500/10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-yellow-500/20 rounded-full">
                      <Database className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Academy Data Migration</p>
                      <p className="text-sm text-slate-400">Update existing enrollments with payment tracking & session balance fields</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => runAcademyMigration("dry_run")}
                      disabled={runningAcademyMigration}
                      variant="outline"
                      className="border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/20"
                      data-testid="academy-migration-preview-btn"
                    >
                      {runningAcademyMigration && academyMigrationMode === "dry_run" ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Preview Changes
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => runAcademyMigration("live")}
                      disabled={runningAcademyMigration}
                      className="bg-yellow-600 hover:bg-yellow-500 text-black font-semibold"
                      data-testid="academy-migration-run-btn"
                    >
                      {runningAcademyMigration && academyMigrationMode === "live" ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Migrating...
                        </>
                      ) : (
                        <>
                          <Wrench className="w-4 h-4 mr-2" />
                          Run Migration
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Migration Results */}
                {academyMigrationResult && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    {academyMigrationResult.success ? (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          {academyMigrationMode === "live" ? (
                            <CheckCircle className="w-4 h-4 text-emerald-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-blue-400" />
                          )}
                          <span className={`font-semibold ${academyMigrationMode === "live" ? "text-emerald-400" : "text-blue-400"}`}>
                            {academyMigrationMode === "live" ? "Migration Completed!" : "Dry Run Preview"} 
                            ({academyMigrationResult.results?.mode})
                          </span>
                        </div>
                        
                        {/* Summary Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Enrollments Updated</p>
                            <p className="text-lg font-bold text-yellow-400">
                              {academyMigrationResult.results?.summary?.enrollments_updated || 0}
                            </p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Players Linked</p>
                            <p className="text-lg font-bold text-blue-400">
                              {academyMigrationResult.results?.summary?.players_linked || 0}
                            </p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Payment Status Changes</p>
                            <p className="text-lg font-bold text-amber-400">
                              {academyMigrationResult.results?.summary?.payment_status_changes || 0}
                            </p>
                          </div>
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-400">Sessions Assigned</p>
                            <p className="text-lg font-bold text-emerald-400">
                              {academyMigrationResult.results?.summary?.sessions_auto_assigned || 0}
                            </p>
                          </div>
                        </div>
                        
                        {/* Data Overview */}
                        <div className="bg-slate-800/30 p-3 rounded text-sm">
                          <p className="text-slate-300 font-medium mb-2">Data Analyzed:</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <span className="text-slate-400">Players: <span className="text-white">{academyMigrationResult.results?.summary?.total_players || 0}</span></span>
                            <span className="text-slate-400">Enrollments: <span className="text-white">{academyMigrationResult.results?.summary?.total_enrollments || 0}</span></span>
                            <span className="text-slate-400">Attendance Records: <span className="text-white">{academyMigrationResult.results?.summary?.total_attendance_records || 0}</span></span>
                            <span className="text-slate-400">Terms: <span className="text-white">{academyMigrationResult.results?.summary?.total_terms || 0}</span></span>
                          </div>
                        </div>
                        
                        {/* Enrollment Update Details */}
                        {academyMigrationResult.results?.details?.find(d => d.step === "Update Enrollment Session Balances")?.changes?.length > 0 && (
                          <div className="bg-slate-800/30 p-3 rounded">
                            <p className="text-slate-300 font-medium mb-2 text-sm">Enrollment Updates:</p>
                            <div className="max-h-48 overflow-y-auto text-xs space-y-1">
                              {academyMigrationResult.results.details.find(d => d.step === "Update Enrollment Session Balances").changes.slice(0, 20).map((change, i) => (
                                <div key={i} className="flex justify-between items-center bg-slate-900/50 p-2 rounded">
                                  <span className="text-white font-medium">{change.player_name}</span>
                                  <div className="flex gap-3 text-slate-400">
                                    <span>{change.sessions_per_week} sess/wk</span>
                                    <span>Purchased: {change.sessions_purchased}</span>
                                    <span>Used: {change.sessions_used}</span>
                                    <span>Remaining: {change.sessions_remaining}</span>
                                    <span className={change.payment_status_change?.includes("→") ? "text-amber-400" : "text-slate-500"}>
                                      {change.payment_status_change}
                                    </span>
                                  </div>
                                </div>
                              ))}
                              {academyMigrationResult.results.details.find(d => d.step === "Update Enrollment Session Balances").changes.length > 20 && (
                                <div className="text-center text-slate-500 py-2">
                                  ...and {academyMigrationResult.results.details.find(d => d.step === "Update Enrollment Session Balances").changes.length - 20} more
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Prompt to run live if dry run */}
                        {academyMigrationMode === "dry_run" && academyMigrationResult.results?.summary?.enrollments_updated > 0 && (
                          <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                            <p className="text-sm text-amber-300">
                              This was a preview only. Click <strong>"Run Migration"</strong> to apply these changes to your production data.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-red-400">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Error: {academyMigrationResult.error}</span>
                      </div>
                    )}
                  </div>
                )}
              </Card>
              
              {/* Bulk Attendance Import Card */}
              <Card className="p-4 mb-6 border rounded-lg border-emerald-500/30 bg-emerald-500/10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-emerald-500/20 rounded-full">
                      <FileSpreadsheet className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Bulk Attendance Import</p>
                      <p className="text-sm text-slate-400">Import attendance records from CSV/Excel file</p>
                    </div>
                  </div>
                  
                  {/* File Upload */}
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleAttendanceFileChange}
                      className="text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-500/20 file:text-emerald-400 hover:file:bg-emerald-500/30"
                      data-testid="attendance-file-input"
                    />
                    {attendanceFile && (
                      <span className="text-sm text-emerald-400">
                        Selected: {attendanceFile.name}
                      </span>
                    )}
                  </div>
                  
                  {/* Expected Format Info */}
                  <div className="bg-slate-800/50 p-3 rounded text-xs text-slate-400">
                    <p className="font-medium text-slate-300 mb-1">Expected columns:</p>
                    <code className="text-emerald-400">name, training_date, present, age_category, paid (optional)</code>
                    <p className="mt-1">• Status values: present/absent/mc (or yes/no)</p>
                    <p>• Date formats: YYYY-MM-DD, DD/MM/YYYY, etc.</p>
                  </div>
                  
                  {/* Options */}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updateBalanceOnImport}
                        onChange={(e) => setUpdateBalanceOnImport(e.target.checked)}
                        className="rounded border-slate-600"
                      />
                      <span className="text-slate-300">Update session balances</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={updatePaymentOnImport}
                        onChange={(e) => setUpdatePaymentOnImport(e.target.checked)}
                        className="rounded border-slate-600"
                      />
                      <span className="text-slate-300">Update payment status from file</span>
                    </label>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button
                      onClick={previewAttendanceImport}
                      disabled={!attendanceFile || previewingAttendance}
                      variant="outline"
                      className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/20"
                      data-testid="attendance-preview-btn"
                    >
                      {previewingAttendance ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Eye className="w-4 h-4 mr-2" />
                          Preview Import
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={executeAttendanceImport}
                      disabled={!attendancePreview || importingAttendance}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                      data-testid="attendance-import-btn"
                    >
                      {importingAttendance ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Import Attendance
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Preview Results */}
                  {attendancePreview && (
                    <div className="mt-2 border-t border-white/10 pt-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-4 h-4 text-blue-400" />
                        <span className="font-semibold text-blue-400">Preview Results</span>
                      </div>
                      
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                        <div className="bg-slate-800/50 p-2 rounded">
                          <p className="text-slate-400">Total Rows</p>
                          <p className="text-lg font-bold text-white">{attendancePreview.summary.total_rows}</p>
                        </div>
                        <div className="bg-slate-800/50 p-2 rounded">
                          <p className="text-slate-400">Matched Players</p>
                          <p className="text-lg font-bold text-emerald-400">{attendancePreview.summary.matched_players}</p>
                        </div>
                        <div className="bg-slate-800/50 p-2 rounded">
                          <p className="text-slate-400">Unmatched</p>
                          <p className={`text-lg font-bold ${attendancePreview.summary.unmatched_players > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                            {attendancePreview.summary.unmatched_players}
                          </p>
                        </div>
                        <div className="bg-slate-800/50 p-2 rounded">
                          <p className="text-slate-400">Issues</p>
                          <p className={`text-lg font-bold ${attendancePreview.summary.issues_count > 0 ? 'text-red-400' : 'text-slate-500'}`}>
                            {attendancePreview.summary.issues_count}
                          </p>
                        </div>
                      </div>
                      
                      {/* Unmatched Names Warning */}
                      {attendancePreview.summary.unmatched_names?.length > 0 && (
                        <div className="bg-amber-500/10 border border-amber-500/30 p-3 rounded mb-3">
                          <p className="text-sm font-medium text-amber-400 mb-1">
                            <AlertTriangle className="w-4 h-4 inline mr-1" />
                            Unmatched player names (will be skipped):
                          </p>
                          <div className="text-xs text-amber-300/80 flex flex-wrap gap-2">
                            {attendancePreview.summary.unmatched_names.slice(0, 10).map((name, i) => (
                              <span key={i} className="bg-amber-500/20 px-2 py-0.5 rounded">{name}</span>
                            ))}
                            {attendancePreview.summary.unmatched_names.length > 10 && (
                              <span className="text-amber-400">+{attendancePreview.summary.unmatched_names.length - 10} more</span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Preview Table */}
                      <div className="bg-slate-800/30 rounded max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-900/50 sticky top-0">
                            <tr>
                              <th className="px-2 py-1 text-left text-slate-400">Row</th>
                              <th className="px-2 py-1 text-left text-slate-400">Name</th>
                              <th className="px-2 py-1 text-left text-slate-400">Date</th>
                              <th className="px-2 py-1 text-left text-slate-400">Status</th>
                              <th className="px-2 py-1 text-left text-slate-400">Matched</th>
                            </tr>
                          </thead>
                          <tbody>
                            {attendancePreview.preview.slice(0, 20).map((record, i) => (
                              <tr key={i} className={`border-t border-slate-700/50 ${record.matched ? '' : 'bg-red-500/10'}`}>
                                <td className="px-2 py-1 text-slate-500">{record.row}</td>
                                <td className="px-2 py-1 text-white">{record.name}</td>
                                <td className="px-2 py-1 text-slate-300">{record.date}</td>
                                <td className="px-2 py-1">
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                    record.status === 'present' ? 'bg-emerald-500/20 text-emerald-400' :
                                    record.status === 'absent' ? 'bg-red-500/20 text-red-400' :
                                    'bg-blue-500/20 text-blue-400'
                                  }`}>
                                    {record.status}
                                  </span>
                                </td>
                                <td className="px-2 py-1">
                                  {record.matched ? (
                                    <CheckCircle className="w-3 h-3 text-emerald-400" />
                                  ) : (
                                    <X className="w-3 h-3 text-red-400" />
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {attendancePreview.preview.length > 20 && (
                          <p className="text-center text-xs text-slate-500 py-2">
                            Showing first 20 of {attendancePreview.all_records_count} records
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Import Results */}
                  {attendanceImportResult && (
                    <div className="mt-2 border-t border-white/10 pt-4">
                      {attendanceImportResult.success ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-semibold">Import Completed Successfully!</span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-400">Created</p>
                              <p className="text-lg font-bold text-emerald-400">{attendanceImportResult.results.attendance_created}</p>
                            </div>
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-400">Duplicates Skipped</p>
                              <p className="text-lg font-bold text-slate-400">{attendanceImportResult.results.attendance_skipped_duplicate}</p>
                            </div>
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-400">No Match Skipped</p>
                              <p className="text-lg font-bold text-amber-400">{attendanceImportResult.results.attendance_skipped_no_match}</p>
                            </div>
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-400">Balances Updated</p>
                              <p className="text-lg font-bold text-blue-400">{attendanceImportResult.results.session_balance_updated}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-400">
                          <AlertTriangle className="w-4 h-4" />
                          <span>Error: {attendanceImportResult.error}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Academy Communication Tools Card */}
              <Card className="p-4 mb-6 border rounded-lg border-blue-500/30 bg-blue-500/10">
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-500/20 rounded-full">
                      <Mail className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="font-bold text-white">Academy Communication Tools</p>
                      <p className="text-sm text-slate-400">Send invoices, reminders, and notices to parents</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Unified Payment Command Centre — replaces scattered Send Invoice + Payment Reminders tiles */}
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-emerald-500/40">
                      <h5 className="font-semibold text-white text-sm mb-2">Payment Command Centre</h5>
                      <p className="text-xs text-slate-400 mb-3">Single source of truth — approve claims, raise disputes, send invoices & reminders, handle partials.</p>
                      <Link to="/admin/payments" data-testid="admin-panel-payments-hub-link">
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Open /admin/payments
                        </Button>
                      </Link>
                    </div>

                    {/* Term Payments — registration + payment status per term.
                        Feb 2026 fix for the "payments all over the place"
                        complaint: one screen for "who registered, who paid,
                        who's pending" for any selected term, with CSV export
                        and duplicate-pending flagging. */}
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-blue-500/40">
                      <h5 className="font-semibold text-white text-sm mb-2">Term Payments</h5>
                      <p className="text-xs text-slate-400 mb-3">Per-term roster: registered / paid / pending — CSV export and duplicate-pending flagging built in.</p>
                      <Link to="/admin/term-payments" data-testid="admin-panel-term-payments-link">
                        <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-500 text-white">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Open /admin/term-payments
                        </Button>
                      </Link>
                    </div>

                    {/* Pending Payment Cleanup — fix for the "Ben Olbourne x5"
                        case. Lets admin bulk-cancel duplicate pending rows or
                        manually cancel one-offs (test rows, abandoned QR
                        visits) without touching real paid transactions. */}
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-rose-500/40">
                      <h5 className="font-semibold text-white text-sm mb-2">Pending Payment Cleanup</h5>
                      <p className="text-xs text-slate-400 mb-3">Delete duplicate / abandoned pending payments. Soft-cancel with audit; paid txns are protected.</p>
                      <Link to="/admin/pending-cleanup" data-testid="admin-panel-pending-cleanup-link">
                        <Button size="sm" className="w-full bg-rose-600 hover:bg-rose-500 text-white">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Open /admin/pending-cleanup
                        </Button>
                      </Link>
                    </div>
                    
                    {/* Academy Settings */}
                    <div className="bg-slate-800/50 p-3 rounded-lg border border-purple-500/30">
                      <h5 className="font-semibold text-white text-sm mb-2">Coach Visibility</h5>
                      <p className="text-xs text-slate-400 mb-3">Toggle payment status for coaches</p>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/20"
                        onClick={async () => {
                          try {
                            const currentSettings = await axios.get(`${API}/academy/admin/academy-settings`, { withCredentials: true });
                            const current = currentSettings.data.show_payment_status_to_coaches || false;
                            await axios.put(`${API}/academy/admin/academy-settings`, 
                              { show_payment_status_to_coaches: !current },
                              { withCredentials: true }
                            );
                            toast.success(`Coach payment visibility ${!current ? 'enabled' : 'disabled'}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Failed to update setting");
                          }
                        }}
                        data-testid="toggle-coach-visibility-btn"
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Toggle Setting
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Member Management - Section Only */}
                {canAccessSection && (
                <Link to="/member-management">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <UserCog className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">Member Management</h4>
                          <p className="text-sm text-slate-400">Edit roles, suspend/reactivate members</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* Data Management */}
                <Link to="/academy/data-management">
                  <Card className="p-6 bg-slate-800/50 border border-teal-500/30 hover:border-teal-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-teal-500/20 rounded-full">
                          <FileSpreadsheet className="w-6 h-6 text-teal-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-teal-400 transition-colors">Data Management</h4>
                          <p className="text-sm text-slate-400">Import/Export Academy & Section data</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-teal-400" />
                    </div>
                  </Card>
                </Link>

                {/* Account Merge Tool - Section Only */}
                {canAccessSection && (
                <Card 
                  className="p-6 bg-slate-800/50 border border-orange-500/30 hover:border-orange-500 rounded-lg transition-all cursor-pointer group"
                  onClick={() => {
                    fetchPotentialDuplicates();
                    openMergeDialog();
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center justify-center w-12 h-12 bg-orange-500/20 rounded-full">
                        <Users className="w-6 h-6 text-orange-400" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-white group-hover:text-orange-400 transition-colors">Merge Duplicate Accounts</h4>
                        <p className="text-sm text-slate-400">Combine duplicate member accounts</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-orange-400" />
                  </div>
                </Card>
                )}

                {/* User Rights - Section Only */}
                {canAccessSection && (
                <Link to="/user-rights">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <UserCog className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">User Rights</h4>
                          <p className="text-sm text-slate-400">Manage permissions & roles</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* Academy Approvals */}
                <Link to="/admin/approvals">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <CheckCircle className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">Academy Approvals</h4>
                          <p className="text-sm text-slate-400">Review registrations & payments</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>

                {/* Registration Pipeline */}
                <Link to="/admin/registration-pipeline">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <ArrowRight className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Registration Pipeline</h4>
                          <p className="text-sm text-slate-400">Track registrations end-to-end</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                {/* Discount Codes */}
                <Link to="/admin/discount-codes">
                  <Card className="p-6 bg-slate-800/50 border border-pink-500/30 hover:border-pink-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-pink-500/20 rounded-full">
                          <Tag className="w-6 h-6 text-pink-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-pink-400 transition-colors">Discount Codes</h4>
                          <p className="text-sm text-slate-400">Create & manage promo codes</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-pink-400" />
                    </div>
                  </Card>
                </Link>

                {/* Merchant Management - Section Only */}
                {canAccessSection && (
                <Link to="/admin/merchants">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <Store className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Merchant Management</h4>
                          <p className="text-sm text-slate-400">View & manage registered merchants</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* Merchant Discounts - Section Only */}
                {canAccessSection && (
                <Link to="/admin/merchant-discounts">
                  <Card className="p-6 bg-slate-800/50 border border-orange-500/30 hover:border-orange-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-orange-500/20 rounded-full">
                          <Tag className="w-6 h-6 text-orange-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-orange-400 transition-colors">Merchant Discounts</h4>
                          <p className="text-sm text-slate-400">Manage merchant member discounts</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-orange-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* Disclaimer Settings - Section Only */}
                {canAccessSection && (
                <Link to="/admin/disclaimer-settings">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <FileText className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Disclaimer Settings</h4>
                          <p className="text-sm text-slate-400">Edit merchant disclaimer & view acceptance report</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* Branding Settings - Section Only */}
                {canAccessSection && (
                <Link to="/admin/branding-settings">
                  <Card className="p-6 bg-slate-800/50 border border-yellow-500/30 hover:border-yellow-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-yellow-500/20 rounded-full">
                          <Palette className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-yellow-400 transition-colors">Branding Settings</h4>
                          <p className="text-sm text-slate-400">Toggle SCC branding on/off</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-yellow-400" />
                    </div>
                  </Card>
                </Link>
                )}

                {/* PDPA / Consent Management */}
                <Link to="/admin/pdpa-settings">
                  <Card className="p-6 bg-slate-800/50 border border-blue-500/30 hover:border-blue-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-blue-500/20 rounded-full">
                          <Shield className="w-6 h-6 text-blue-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-blue-400 transition-colors">PDPA / Consent</h4>
                          <p className="text-sm text-slate-400">Manage consent clauses & acknowledgments</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-blue-400" />
                    </div>
                  </Card>
                </Link>

                {/* Force Logout */}
                <Link to="/admin/force-logout">
                  <Card className="p-6 bg-slate-800/50 border border-red-500/30 hover:border-red-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full">
                          <LogOut className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-red-400 transition-colors">Force Logout</h4>
                          <p className="text-sm text-slate-400">Log out users by group & trigger PDPA</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400" />
                    </div>
                  </Card>
                </Link>

                {/* WhatsApp Settings */}
                <Link to="/admin/whatsapp-settings" data-testid="whatsapp-settings-link">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <MessageCircle className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">WhatsApp Settings</h4>
                          <p className="text-sm text-slate-400">Configure reminders, templates & test sending</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                {/* Fee Matrix */}
                <Link to="/admin/fee-matrix" data-testid="fee-matrix-link">
                  <Card className="p-6 bg-slate-800/50 border border-purple-500/30 hover:border-purple-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-full">
                          <DollarSign className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-purple-400 transition-colors">Fee Matrix</h4>
                          <p className="text-sm text-slate-400">Printable price reference table (1-3 kids × 1-3 sessions)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-purple-400" />
                    </div>
                  </Card>
                </Link>

                {/* Admin Handbook */}
                <Link to="/admin/handbook" data-testid="admin-handbook-link">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <BookOpen className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Admin Handbook</h4>
                          <p className="text-sm text-slate-400">Step-by-step guide for every admin task</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                {/* Coach Handbook */}
                <Link to="/handbook/coach" data-testid="coach-handbook-link">
                  <Card className="p-6 bg-slate-800/50 border border-emerald-500/30 hover:border-emerald-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-full">
                          <BookOpen className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-emerald-400 transition-colors">Coach Handbook</h4>
                          <p className="text-sm text-slate-400">On-pitch + portal guide for coaches (download PDF to share)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-emerald-400" />
                    </div>
                  </Card>
                </Link>

                {/* Parent Handbook */}
                <Link to="/handbook/parent" data-testid="parent-handbook-link">
                  <Card className="p-6 bg-slate-800/50 border border-sky-500/30 hover:border-sky-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-sky-500/20 rounded-full">
                          <BookOpen className="w-6 h-6 text-sky-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-sky-400 transition-colors">Parent Handbook</h4>
                          <p className="text-sm text-slate-400">The guide parents receive — share PDF at onboarding</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-sky-400" />
                    </div>
                  </Card>
                </Link>

                {/* Data Sync */}
                <Link to="/admin/data-sync" data-testid="admin-data-sync-link">
                  <Card className="p-6 bg-slate-800/50 border border-sky-500/30 hover:border-sky-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-sky-500/20 rounded-full">
                          <Database className="w-6 h-6 text-sky-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-sky-400 transition-colors">Data Sync</h4>
                          <p className="text-sm text-slate-400">Heal payment status + auto-enrol kids on rosters (one click)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-sky-400" />
                    </div>
                  </Card>
                </Link>

                {/* Fee Reconciliation */}
                <Link to="/admin/fee-reconciliation" data-testid="admin-fee-reconciliation-link">
                  <Card className="p-6 bg-slate-800/50 border border-amber-500/30 hover:border-amber-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-full">
                          <RefreshCcw className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-amber-400 transition-colors">Fee Reconciliation</h4>
                          <p className="text-sm text-slate-400">Find & fix mis-priced registrations (parents whose membership was verified after they registered)</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-amber-400" />
                    </div>
                  </Card>
                </Link>

                {/* Duplicate Accounts */}
                <Link to="/admin/duplicate-accounts">
                  <Card className="p-6 bg-slate-800/50 border border-red-500/30 hover:border-red-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-red-500/20 rounded-full">
                          <AlertTriangle className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-red-400 transition-colors">Duplicate Accounts</h4>
                          <p className="text-sm text-slate-400">Find & merge duplicate users</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-red-400" />
                    </div>
                  </Card>
                </Link>

                {/* Academy Roster - NEW */}
                <Link to="/admin/academy-roster">
                  <Card className="p-6 bg-slate-800/50 border border-cyan-500/30 hover:border-cyan-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-full">
                          <Users className="w-6 h-6 text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-cyan-400 transition-colors">Academy Roster</h4>
                          <p className="text-sm text-slate-400">Sessions → Players → Parents</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-cyan-400" />
                    </div>
                  </Card>
                </Link>

                {/* Committee Portal */}
                <Link to="/committee">
                  <Card className="p-6 bg-slate-800/50 border border-indigo-500/30 hover:border-indigo-500 rounded-lg transition-all cursor-pointer group">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-12 h-12 bg-indigo-500/20 rounded-full">
                          <Users className="w-6 h-6 text-indigo-400" />
                        </div>
                        <div>
                          <h4 className="font-bold text-lg text-white group-hover:text-indigo-400 transition-colors">Committee Portal</h4>
                          <p className="text-sm text-slate-400">Manage members & meetings</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-600 group-hover:text-indigo-400" />
                    </div>
                  </Card>
                </Link>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit User: {editingUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Name</label>
              <Input
                value={editUserForm.name}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            
            {/* Email */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Email</label>
              <Input
                type="email"
                value={editUserForm.email}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email address"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
              <p className="text-xs text-amber-400 mt-1">⚠️ Changing email will update their login credentials</p>
            </div>
            
            {/* Phone */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Phone</label>
              <Input
                value={editUserForm.phone}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="Phone number"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            
            {/* Membership Number */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Membership Number</label>
              <Input
                value={editUserForm.membership_number}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, membership_number: e.target.value }))}
                placeholder="e.g., P0001"
                className="bg-slate-800/50 border-slate-700 text-white"
              />
            </div>
            
            {/* Role */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Role</label>
              <Select 
                value={editUserForm.role} 
                onValueChange={(v) => setEditUserForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="team_captain">Team Captain</SelectItem>
                  <SelectItem value="vice_captain">Vice Captain</SelectItem>
                  <SelectItem value="captain">Section Captain</SelectItem>
                  <SelectItem value="convenor">Convenor</SelectItem>
                  <SelectItem value="academy_parent">Academy Parent</SelectItem>
                  <SelectItem value="merchant">Merchant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Team */}
            <div>
              <label className="text-sm font-medium mb-2 block text-slate-300">Team</label>
              <Select 
                value={editUserForm.team_id || "__none__"} 
                onValueChange={(v) => setEditUserForm(prev => ({ ...prev, team_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No Team</SelectItem>
                  {teams.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setEditingUser(null)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={saveUserEdit}
                className="flex-1 bg-yellow-500 hover:bg-yellow-400 text-black"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Merge Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-orange-400" />
              Merge Duplicate Accounts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <p className="text-sm text-slate-400">
              Use this tool to merge duplicate accounts. The <strong className="text-orange-400">source account</strong> will be deleted, 
              and its data (email, relationships) will be transferred to the <strong className="text-green-400">target account</strong>.
            </p>

            {/* Potential Duplicates */}
            {potentialDuplicates.length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-amber-400 mb-2">⚠️ Potential Duplicates Found ({potentialDuplicates.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {potentialDuplicates.slice(0, 5).map((dup, idx) => (
                    <div key={idx} className="text-sm text-slate-300 flex items-center justify-between bg-slate-800/50 p-2 rounded">
                      <span>
                        {dup.users.map(u => u.email).join(" ↔ ")}
                        <span className="text-xs text-slate-500 ml-2">({dup.reason})</span>
                      </span>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          // Find full user objects from users array (not the limited duplicate data)
                          const sourceCandidate = dup.users.find(u => !u.membership_number) || dup.users[0];
                          const targetCandidate = dup.users.find(u => u.membership_number) || dup.users[1];
                          setMergeSourceUser(users.find(u => u.id === sourceCandidate.id) || sourceCandidate);
                          setMergeTargetUser(users.find(u => u.id === targetCandidate.id) || targetCandidate);
                        }}
                        className="text-xs"
                      >
                        Select
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingDuplicates && (
              <div className="text-center py-4">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-400 mx-auto" />
                <p className="text-sm text-slate-400 mt-2">Scanning for duplicates...</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Source Account (to be deleted) */}
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-red-400 mb-3">Source Account (will be DELETED)</h4>
                <Select
                  value={mergeSourceUser?.id || ""}
                  onValueChange={(id) => setMergeSourceUser(users.find(u => u.id === id))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-600">
                    <SelectValue placeholder="Select source account..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {users.filter(u => u.id !== mergeTargetUser?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}) {u.membership_number ? `[${u.membership_number}]` : '[No membership #]'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {mergeSourceUser && (
                  <div className="mt-3 text-sm space-y-1">
                    <p className="text-slate-300"><strong>Name:</strong> {mergeSourceUser.name}</p>
                    <p className="text-slate-300"><strong>Email:</strong> {mergeSourceUser.email}</p>
                    <p className="text-slate-300"><strong>Membership #:</strong> {mergeSourceUser.membership_number || 'None'}</p>
                    <p className="text-slate-300"><strong>Phone:</strong> {mergeSourceUser.phone || 'None'}</p>
                  </div>
                )}
              </div>

              {/* Target Account (to be kept) */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-green-400 mb-3">Target Account (will be KEPT)</h4>
                <Select
                  value={mergeTargetUser?.id || ""}
                  onValueChange={(id) => setMergeTargetUser(users.find(u => u.id === id))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-600">
                    <SelectValue placeholder="Select target account..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {users.filter(u => u.id !== mergeSourceUser?.id).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}) {u.membership_number ? `[${u.membership_number}]` : '[No membership #]'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {mergeTargetUser && (
                  <div className="mt-3 text-sm space-y-1">
                    <p className="text-slate-300"><strong>Name:</strong> {mergeTargetUser.name}</p>
                    <p className="text-slate-300"><strong>Email:</strong> {mergeTargetUser.email}</p>
                    <p className="text-slate-300"><strong>Membership #:</strong> {mergeTargetUser.membership_number || 'None'}</p>
                    <p className="text-slate-300"><strong>Phone:</strong> {mergeTargetUser.phone || 'None'}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Merge Options */}
            {mergeSourceUser && mergeTargetUser && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
                <h4 className="font-semibold text-white mb-3">Merge Options</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={mergeUseSourceEmail}
                      onCheckedChange={setMergeUseSourceEmail}
                    />
                    <span className="text-sm text-slate-300">
                      Use email from source account: <strong className="text-orange-400">{mergeSourceUser.email}</strong>
                    </span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={mergeUseSourceName}
                      onCheckedChange={setMergeUseSourceName}
                    />
                    <span className="text-sm text-slate-300">
                      Use name from source account: <strong className="text-orange-400">{mergeSourceUser.name}</strong>
                    </span>
                  </label>
                </div>
                
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded text-sm text-blue-300">
                  <strong>After merge:</strong>
                  <ul className="list-disc list-inside mt-1">
                    <li>Email: {mergeUseSourceEmail ? mergeSourceUser.email : mergeTargetUser.email}</li>
                    <li>Name: {mergeUseSourceName ? mergeSourceUser.name : mergeTargetUser.name}</li>
                    <li>Membership #: {mergeTargetUser.membership_number || 'None'}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowMergeDialog(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={executeMerge}
                disabled={!mergeSourceUser || !mergeTargetUser || mergingAccounts}
                className="flex-1 bg-orange-500 hover:bg-orange-400 text-white"
              >
                {mergingAccounts ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Merging...
                  </>
                ) : (
                  <>Merge Accounts</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function UserCard({ user, teams, onAuthorize, onReject }) {
  const [selectedRole, setSelectedRole] = useState("member");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [memberType, setMemberType] = useState("non-section");

  const handleAuthorize = () => {
    onAuthorize(user.id, selectedRole, selectedTeam || null, memberType);
  };

  const handleReject = () => {
    if (window.confirm(`Are you sure you want to reject ${user.name}'s registration? This will permanently delete their account.`)) {
      onReject(user.id);
    }
  };

  // Get team names for display
  const primaryTeamName = user.primary_team ? teams.find(t => t.id === user.primary_team)?.name : null;
  const secondTeamName = user.second_team ? teams.find(t => t.id === user.second_team)?.name : null;

  return (
    <Card className="p-4 bg-slate-800/50 border border-yellow-500/30 rounded-lg" data-testid={`pending-user-${user.id}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex-1">
          <p className="font-semibold text-white" data-testid={`pending-user-name-${user.id}`}>{user.name}</p>
          <p className="text-sm text-slate-300" data-testid={`pending-user-email-${user.id}`}>{user.email}</p>
          {user.phone && <p className="text-sm text-slate-400">📱 {user.phone}</p>}
          {user.birthdate && <p className="text-sm text-slate-400">🎂 {new Date(user.birthdate).toLocaleDateString()}</p>}
          
          {/* Team Preferences */}
          <div className="mt-2 flex flex-wrap gap-2">
            {primaryTeamName && (
              <span className="inline-flex items-center px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-semibold rounded">
                🎯 Primary: {primaryTeamName}
              </span>
            )}
            {secondTeamName && (
              <span className="inline-flex items-center px-2 py-1 bg-slate-700 text-slate-300 text-xs font-semibold rounded">
                2nd: {secondTeamName}
              </span>
            )}
            {!primaryTeamName && !secondTeamName && (
              <span className="inline-flex items-center px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-semibold rounded">
                ⚠️ No team selected
              </span>
            )}
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          {/* Role dropdown - What role will they have in the club */}
          <div className="flex flex-col">
            <span className="text-xs text-slate-400 mb-1">Role</span>
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-full sm:w-40 bg-slate-800/50 border-slate-700 text-white" data-testid={`role-select-${user.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="non-member">Non-Member</SelectItem>
                <SelectItem value="team_captain">Team Captain</SelectItem>
                <SelectItem value="vice_captain">Vice Captain</SelectItem>
                <SelectItem value="team_admin">Team Admin</SelectItem>
                <SelectItem value="treasurer">Treasurer</SelectItem>
                <SelectItem value="secretary">Secretary</SelectItem>
                <SelectItem value="jsm_representative">JSM Representative</SelectItem>
                <SelectItem value="committee_member">Committee Member</SelectItem>
                <SelectItem value="event_manager">Event Manager</SelectItem>
                <SelectItem value="merchant">Merchant</SelectItem>
                <SelectItem value="captain">Section Captain (Admin)</SelectItem>
                <SelectItem value="convenor">Convenor (Admin)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Member Type dropdown - SCC Membership Status */}
          <div className="flex flex-col">
            <span className="text-xs text-slate-400 mb-1">SCC Membership</span>
            <Select value={memberType} onValueChange={setMemberType}>
              <SelectTrigger className="w-full sm:w-48 bg-slate-800/50 border-slate-700 text-white" data-testid={`member-type-select-${user.id}`}>
                <SelectValue placeholder="Member Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="section">✅ Section Member</SelectItem>
                <SelectItem value="non-section">✅ Non-Section (SCC Member)</SelectItem>
                <SelectItem value="non-member">❌ Non-Member</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {["team_captain", "vice_captain"].includes(selectedRole) && (
            <div className="flex flex-col">
              <span className="text-xs text-slate-400 mb-1">Team</span>
              <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                <SelectTrigger className="w-full sm:w-40 bg-slate-800/50 border-slate-700 text-white" data-testid={`team-select-${user.id}`}>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button
            onClick={handleAuthorize}
            className="bg-emerald-600 hover:bg-emerald-500/100 text-white font-semibold"
            data-testid={`authorize-button-${user.id}`}
          >
            Approve
          </Button>
          <Button
            onClick={handleReject}
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10 font-semibold"
            data-testid={`reject-button-${user.id}`}
          >
            Reject
          </Button>
        </div>
      </div>
    </Card>
  );
}